import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/*
 * IC FunFlow — Internationalisation Dictionary
 * ---------------------------------------------------------------
 * Supported languages: English (en), French (fr), Spanish (es),
 * German (de), Italian (it).
 *
 * NOTE: Activity NAMES and DESCRIPTIONS are deliberately NOT translated —
 * those are user-authored content that resorts edit manually in Activity
 * Templates. Only the application interface lives here.
 */

const translations = {
  // ─────────────────────────────────────────────────────────────────────
  // ENGLISH
  // ─────────────────────────────────────────────────────────────────────
  en: {
    // Common / Navigation
    home: 'Home',
    today: 'Today',
    weekly: 'Weekly Program',
    week: 'Week',
    profile: 'Profile',
    bookings: 'Bookings',
    logout: 'Logout',
    save: 'Save',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    remove: 'Remove',
    loading: 'Loading…',
    search: 'Search',
    searchPlaceholder: 'Search by name, room…',
    noResults: 'No results',
    back: 'Back',
    done: 'Done',
    next: 'Next',
    previous: 'Previous',
    submit: 'Submit',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    refresh: 'Refresh',
    retry: 'Retry',
    saving: 'Saving…',
    saved: 'Saved',
    settings: 'Settings',
    language: 'Language',

    // Auth / Registration
    welcomeTo: 'Welcome to',
    signInSubtitle: "Sign in to dive into today's resort program",
    login: 'Login',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    signIn: 'Sign In',
    createAccount: 'Create Account',
    fullName: 'Full Name',
    mainGuestName: 'Main Guest Name',
    roomNumber: 'Room Number',
    occupants: 'Occupants',
    occupant: 'Occupant',
    addOccupant: 'Add Occupant',
    category: 'Category',
    adult: 'Adult',
    kid: 'Kid',
    checkInDate: 'Check-in Date',
    checkOutDate: 'Check-out Date',
    invalidCredentials: 'Invalid email or password',
    accountCreated: 'Account created!',

    // Dashboard sections (Guest)
    welcomeBack: 'Welcome back',
    happeningNow: 'HAPPENING NOW!',
    todayActivities: "Today's Activities",
    adultsActivities: 'Adults Activities Today',
    kidsActivities: 'Kids Activities Today',
    eveningActivities: 'Evening Activities',
    specialEvents: 'Special Events',
    luckyDraw: 'Lucky',
    noAdultActivities: 'No adult activities scheduled for today',
    noKidsActivities: 'No kids activities scheduled for today',
    noActivities: 'No activities scheduled',
    noEveningShows: 'No evening shows scheduled',
    upcomingEvents: 'Upcoming Special Events',
    myBookings: 'My Bookings',
    noBookings: 'No bookings yet',
    resortNotes: 'Resort Notes',

    // Activities
    activities: 'Activities',
    allActivities: 'All Activities',
    activity: 'Activity',
    location: 'Location',
    time: 'Time',
    startTime: 'Start time',
    endTime: 'End time',
    animator: 'Animator',
    animators: 'Animators',
    description: 'Description',
    capacity: 'Capacity',
    spotsLeft: 'spots left',
    full: 'Full',
    bookNow: 'Book Now',
    booked: 'Booked',
    signedUp: 'Signed Up',
    cancelBooking: 'Cancel Booking',
    bookActivity: 'Book Activity',
    bookingConfirmed: 'Booking confirmed!',
    bookingCancelled: 'Booking cancelled',
    liveNow: 'LIVE NOW',
    starting: 'Starting',
    startsIn: 'Starts in',
    minutes: 'minutes',
    showMore: 'Show more',
    showLess: 'Show less',

    // Scoring
    liveScoreboard: 'Live Scoreboard',
    participants: 'participants',
    winner: 'Winner',
    score: 'Score',
    team: 'Team',
    round: 'Round',
    total: 'Total',
    rank: 'Rank',
    tournament: 'Tournament',
    results: 'Results',
    finalRanking: 'Final Ranking',
    points: 'Points',

    // Feedback
    feedback: 'Feedback',
    submitFeedback: 'Submit Feedback',
    leaveFeedback: 'Leave Feedback',
    rateActivity: 'Rate this activity',
    excellent: 'Excellent',
    good: 'Good',
    bad: 'Bad',
    comment: 'Comment',
    feedbackThanks: 'Thanks for your feedback!',

    // Notifications
    notifications: 'Notifications',
    noNotifications: 'No notifications',
    notificationSettings: 'Notification Settings',
    notificationPreferences: 'Notification Preferences',
    enablePushNotifications: 'Enable Push Notifications',
    enablePushDesc: 'When off, you will not receive any push notifications. You can turn this back on anytime.',
    browserPermission: 'Browser Permission',
    browserPermissionBlocked: 'Blocked in browser settings',
    browserPermissionDesc: 'Allow this browser to receive push notifications',
    activityReminders: 'Activity Reminders',
    activityRemindersDesc: 'Get a heads-up when an activity is about to start',
    liveActivityNotifications: 'Live Activity Notifications',
    liveActivityDesc: 'Be notified the moment an activity goes live',
    generalAnnouncements: 'General Announcements',
    generalAnnouncementsDesc: 'Important resort-wide announcements from the team',
    activityAssignments: 'Activity Assignments',
    activityAssignmentsDesc: "When you're assigned to a new activity",
    scheduleChanges: 'Schedule Changes',
    scheduleChangesDesc: 'Changes to timing, location, or staff for your activities',
    couldNotSavePref: 'Could not save preference. Please try again.',
    loadingPrefs: 'Loading notification preferences…',
    backToDashboard: 'Back to dashboard',

    // Admin / Staff
    adminDashboard: 'Admin Dashboard',
    animatorDashboard: 'Animator Dashboard',
    chiefAnimator: 'Chief Animator',
    liveMonitor: 'Live Monitor',
    addAnimator: 'Add Animator',
    evaluate: 'Evaluate',
    newSession: 'New Session',
    draw: 'Draw',
    scoreSheetsLibrary: 'Score Sheets Library',
    resultsArchive: 'Results Archive',
    categoryImages: 'Category Images',
    devMaintenance: 'Developer / Maintenance',
    dedupePushTokens: 'Dedupe Push Tokens',
    migrateImages: 'Migrate Images',
    enablePush: 'Enable Push',
    testPush: 'Test Push',
    performanceMode: 'Performance Mode',
    performanceModeOn: 'Performance Mode ON',
    performanceModeOff: 'Performance Mode OFF',
    showAdvancedTools: 'Show advanced tools',
    hideAdvancedTools: 'Hide advanced tools',
    devMaintenanceHint: 'Maintenance tools — only use when troubleshooting.',
    team: 'Team',
    totalActivities: 'total activities',
    activeGuests: 'Active Guests',
    pendingApprovals: 'Pending Approvals',
    sendNotification: 'Send Notification',
    notificationTitle: 'Title',
    notificationMessage: 'Message',
    audience: 'Audience',
    audienceAll: 'Staff and Guests',
    audienceGuests: 'Guests only',
    audienceStaff: 'Staff only',

    // Calendar Planner
    calendarPlanner: 'Calendar Planner',
    addActivity: 'Add Activity',
    selectDate: 'Select date',
    selectTime: 'Select time',
    activityName: 'Activity name',
    saveOnly: 'Save Only',
    saveAndNotify: 'Save & Notify',
    programSaved: 'Program saved!',
    notifyingGuests: 'Notifying guests…',

    // Room Ranges
    roomRanges: 'Room Ranges',
    roomRangesDesc: 'Configure valid room number ranges for guest registration',
    addRange: 'Add Range',
    saveRanges: 'Save Ranges',
    start: 'Start',
    end: 'End',

    // Errors / Toasts
    errorGeneric: 'Something went wrong. Please try again.',
    errorNetwork: 'Network error. Check your connection.',
    errorPermission: 'You do not have permission to do that.',
    sessionExpired: 'Session expired — please sign in again.',
    copied: 'Copied to clipboard',

    // Guest UI — added Feb 2026 for full multilingual coverage
    hotelServices: 'Hotel Services',
    view: 'View',
    liveActivitiesTitle: 'Live Activities',
    noLiveActivitiesNow: 'No live activities at the moment.',
    communityMode: 'Community Mode',
    appInformation: 'App Information',
    version: 'Version',
    activityFull: 'Activity Full',
    joinNow: 'Join Now',
    notesLabel: 'Notes',
    noActivitiesScheduled: 'No activities scheduled',
    pickLuckyTitle: 'Pick Your Lucky Numbers (1-100)',
    dailyLuckyNumber: 'Daily Lucky Number',
    luckyRule1: 'Each person picks their own number from 1 to 100',
    luckyRule2: 'Add entries for kids and family members',
    luckyRule3: 'Each room can submit up to 4 entries per day',
    luckyRule4: 'Admin draws the winning number at end of day',
    luckyRule5: 'Closest number to the drawn number wins!',
    howWasActivity: 'How was the activity?',
    participantDetails: 'Participant Details',
    nameField: 'Name',
    tapPhotoChange: 'Tap the photo to change it',
    closedToday: 'Closed today',
    noServicesScheduled: 'No services scheduled',
    menu: 'Menu',
    markAllRead: 'Mark all read',
    tapToRead: 'TAP TO READ',
    noNotificationsYet: 'No notifications yet',
    willNotifyYou: "We'll notify you when something happens",
    thisWeekProgram: "This Week's Program",
    liveBadge: 'LIVE',
    justNow: 'Just now',
    musicStoreDescription: 'Discover our original resort songs, kids disco music, pool party tracks, and holiday vibes.',
    exploreMusicStore: 'Explore Music Store',
    luckyLockedTitle: 'Your Lucky Numbers are Locked',
    luckyLockedMessage: '🎲 The Lucky Draw is Ready! Stay tuned — the animation team will begin the draw shortly.',
    luckyTimeRemaining: 'Time remaining to choose your Lucky Numbers',
    luckyPickHint: 'Pick up to 4 lucky numbers (1–100) for your room',
    editNumbers: 'Edit Numbers',
    pickNumbers: 'Pick Numbers',
    welcomeGreeting: 'Welcome',
    // Music Store
    musicStoreTitle: 'Music Store',
    musicStoreSubtitle: 'Original tracks from our entertainment team. Keep them forever.',
    exitMusicStore: 'Exit Music Store',
    loadingMusic: 'Loading music…',
    storeOpeningSoon: 'Store opening soon',
    storeNewTracksMsg: 'New tracks will appear here as the team publishes them.',
    myPurchases: 'My Purchases',
    myPurchasesSubtitle: "Re-download any song you've bought — no extra charge.",
    loadingPurchases: 'Loading your purchases…',
    noPurchases: "You haven't bought any songs yet.",
    purchased: 'Purchased',
    buyBtn: 'Buy',
    downloadBtn: 'Download',
    orderPrefix: 'Order',
    buyPrefix: 'Buy',
    buyReviewMsg: 'Review your purchase and accept the personal-use terms to continue.',
    songLabel: 'Song',
    priceLabel: 'Price',
    buyerLabel: 'Buyer',
    secureCheckoutBy: 'Secure checkout by',
    secureCheckoutTail: "You'll be redirected to enter your payment details.",
    payLabel: 'Pay',
    couldNotLoadStore: 'Could not load the Music Store. Please try again.',
    previewUnavailable: 'Preview is unavailable right now.',
    pleaseAcceptAgreement: 'Please accept the personal-use agreement to continue.',
    couldNotStartPayment: 'Could not start payment.',
    personalUseAgreementText: 'I agree that this song is purchased for personal use only. I will not upload, resell, redistribute, share publicly, or use it commercially without written permission from IC FunFlow.',
    // Lucky Number picker
    luckyInstructions: 'Each person picks their own lucky number. Add entries for family members!',
    deadlineLabel: 'Deadline',
    deadlineClosed: '(CLOSED)',
    yourRoomsEntriesToday: "Your Room's Entries Today:",
    roomLabel: 'Room',
    saveBtn: 'Save',
    editBtn: 'Edit',
    submissionsClosed: 'Submissions closed for today!',
    submissionsClosedMsg: 'The deadline was {time}. Good luck with your existing entries!',
    allLuckySubmitted: 'All lucky numbers submitted!',
    goodLuckWinners: 'Good luck! Winners will be announced at the end of the day.',
    editUntilMsg: 'You can still edit your numbers above until {time}.',
    roomEntriesToday: 'Room entries today',
    entriesRemaining: 'entry(ies) remaining for your room',
    roomLimitReached: 'Your room has reached the daily limit of 4 entries',
    newEntryPrefix: 'New Entry',
    luckyNumberLabel: 'Lucky Number (1-100)',
    addAnotherParticipant: 'Add Another Participant',
    submitLuckyOne: 'Submit Lucky Number',
    submitLuckyMany: 'Submit {n} Lucky Numbers',
    todaysLuckyNumber: "Today's Lucky Number",
    winningNumber: 'Winning number',
    congratulationsPrefix: 'Congratulations',
    // Install banner
    installTitle: 'Install IC FunFlow for the best experience',
    installBtn: 'Install',
    installLater: 'Later',
    iosInstallHint: 'Tap Share → "Add to Home Screen"',
    howItWorks: 'How it works:',
    luckyRuleEditUntil: 'You can edit your numbers until {time}',
    // Toasts (guest-facing)
    toastPushEnabled: 'Push notifications enabled!',
    toastPushBlocked: 'Notifications blocked. Please enable in browser settings.',
    toastPushDisabled: 'Push notifications disabled',
    toastSavePrefFailed: 'Failed to save preference',
    toastSignupCancelled: 'Signup cancelled',
    toastPleaseRate: 'Please select a rating',
    toastFeedbackThanks: 'Thank you for your feedback!',
    toastFeedbackFailed: 'Failed to submit feedback',
    toastInvalidLucky: 'Please enter a valid number (1-100)',
    toastLuckyUpdated: 'Lucky number updated!',
    toastLuckyCancelled: 'Lucky number entry cancelled',
    // Misc guest surfaces
    addToHomeScreenHint: 'Add to home screen for the best experience',
    awayModeMessage: 'To begin a new holiday, please visit our Entertainment Team.',
    todayBadge: 'Today',
    commentsPlaceholder: 'Any comments? (optional)',
    menuViewer: 'Menu Viewer',
    notificationDetails: 'Notification Details',
    // Activity category badges
    catAdults: 'Adults',
    catKids: 'Kids',
    catAllAges: 'All Ages',
    catEveningAdults: 'Evening Adults',
    catEveningKids: 'Evening Kids',
    catKidsClub: 'Kids Club',
    catSports: 'Sports',
    catDj: 'DJ',
    // Welcome page (added Feb 2026 — first-visit language selector)
    resortEntertainment: 'Resort Entertainment',
    welcomeHeroLine1: 'Your stay,',
    welcomeHeroLine2: 'amplified.',
    welcomeHeroSubtitle: 'Sign in to your room account, create one for your stay, or just look around — no account needed.',
    welcomeChipDaily: 'Daily programme',
    welcomeChipEvents: 'Special events',
    welcomeChipMusic: 'Music store',
    welcomeSigninDesc: 'Returning guest or staff member.',
    welcomeCreateDesc: 'First time? Set up your stay account.',
    welcomeExploreTitle: 'Explore as Visitor',
    welcomeExploreDesc: 'No sign-up needed. Browse programme, events & store.',
    welcomeFooterHint: 'Bookings, feedback and lucky number require a stay account. Music purchases work as a visitor too.',
    // Auth page (added Feb 2026 — carry language across login/register)
    signingIn: 'Signing in…',
    creatingAccount: 'Creating account…',
    joinUs: 'Join Us!',
    createAccountSubtitle: 'Create your account to get started',
    iAmA: 'I am a:',
    guest: 'Guest',
    animatorRole: 'Animator',
    // Explore as Visitor (added Feb 2026 — carry Welcome language into /explore)
    exploreBrand: 'IC FunFlow — Explore',
    exploreBackWelcome: 'Welcome',
    exploreJoin: 'Join',
    exploreTabProgramme: 'Programme',
    exploreTabFoodBeverage: 'Food & Beverage',
    exploreTabEvents: 'Special Events',
    exploreTabStore: 'Music Store',
    exploreTabNews: 'News & Updates',
    exploreTodaysProgramme: "Today's Programme",
    exploreLoading: 'Loading…',
    // Hotel selection screen (Feb 2026 — multi-hotel Explore)
    exploreSelectTitle: 'Which hotel are you exploring?',
    exploreSelectSubtitle: 'Choose a hotel to explore',
    exploreSelectDesc: 'Programme, dining, events and music are curated per property. Pick one to see what\u2019s on right now.',
    exploreSelectLoading: 'Loading hotels…',
    exploreSelectLoadError: 'Could not load hotels right now.',
    exploreSelectEmpty: 'No active hotels are available right now. Please check back soon.',
    exploreSelectContinue: 'Continue',
    exploreSelectSearchPlaceholder: 'Hotel name',
    exploreSelectSearchLabel: 'Hotel name',
    exploreSelectClear: 'Clear',
    exploreSelectNoMatches: 'No matching hotel. Check the spelling or ask reception.',
    exploreSelectStaleHotel: 'That hotel is no longer available — please pick another.',
    exploreChangeHotel: 'Change',
    exploreDisabledTitle: 'Explore is not available at this hotel',
    exploreDisabledDesc: 'Please contact the reception for current programme and services.',
    exploreNoSectionsTitle: 'No sections to show yet',
    exploreNoSectionsDesc: 'This hotel hasn\u2019t published any visitor content yet.',
    exploreLoadingStore: 'Loading store…',
    exploreLoadingEvents: 'Loading events…',
    exploreLoadingFB: 'Loading Food & Beverage…',
    exploreLoadingUpdates: 'Loading updates…',
    exploreNothingOnTodayTitle: 'Nothing on today',
    exploreNothingOnTodayDesc: "Today's programme has not been published yet. Please check back later or visit our Entertainment Team for today's activities.",
    exploreUnlockWeeklyTitle: 'Create an account to unlock the full weekly programme.',
    exploreUnlockWeeklyDesc: 'See every activity for the next 14 days and book directly from your guest dashboard.',
    exploreStoreEmptyTitle: 'Store opening soon',
    exploreStoreEmptyDesc: 'No tracks are available yet. Please check back later.',
    exploreEventsEmptyTitle: 'No special events scheduled',
    exploreEventsEmptyDesc: 'Live shows and themed evenings will appear here as soon as the team publishes them.',
    exploreEventBadge: 'Special Event',
    exploreFBComingSoonTitle: 'Coming soon',
    exploreFBComingSoonDesc: 'Food & Beverage information will appear here as the resort publishes it.',
    exploreFBNothingPublished: 'Nothing published yet — check back soon.',
    exploreFBNoInfo: 'No information published yet.',
    exploreNewsEmptyTitle: 'Nothing new just yet',
    exploreNewsEmptyDesc: 'When the team sends a Community announcement it will appear here.',
    exploreNewsUpdateBadge: 'Update',
    // Food & Beverage card labels (shared DepartmentItemCard — Feb 2026)
    fbClosed: 'Closed',
    fbClosedNow: 'Closed now',
    fbOpenNow: 'Open now',
    fbOpensAt: 'Opens at',
    fbViewWeeklySchedule: 'View weekly schedule',
    fbHideWeeklySchedule: 'Hide weekly schedule',
    fbViewMenu: 'View Menu',
    fbMenus: 'Menus',
    fbMenu: 'Menu',
    fbWebsite: 'Website',
    fbMenuLink: 'Menu link',
    fbDressCode: 'Dress code',
    fbService: 'Service',
  },

  // ─────────────────────────────────────────────────────────────────────
  // FRANÇAIS
  // ─────────────────────────────────────────────────────────────────────
  fr: {
    home: 'Accueil',
    today: "Aujourd'hui",
    weekly: 'Programme Hebdomadaire',
    week: 'Semaine',
    profile: 'Profil',
    bookings: 'Réservations',
    logout: 'Déconnexion',
    save: 'Enregistrer',
    saveChanges: 'Enregistrer les modifications',
    cancel: 'Annuler',
    close: 'Fermer',
    delete: 'Supprimer',
    edit: 'Modifier',
    add: 'Ajouter',
    remove: 'Retirer',
    loading: 'Chargement…',
    search: 'Rechercher',
    searchPlaceholder: 'Rechercher par nom, chambre…',
    noResults: 'Aucun résultat',
    back: 'Retour',
    done: 'Terminé',
    next: 'Suivant',
    previous: 'Précédent',
    submit: 'Envoyer',
    confirm: 'Confirmer',
    yes: 'Oui',
    no: 'Non',
    refresh: 'Actualiser',
    retry: 'Réessayer',
    saving: 'Enregistrement…',
    saved: 'Enregistré',
    settings: 'Paramètres',
    language: 'Langue',

    welcomeTo: 'Bienvenue sur',
    signInSubtitle: "Connectez-vous pour découvrir le programme du jour",
    login: 'Connexion',
    register: 'Inscription',
    email: 'E-mail',
    password: 'Mot de passe',
    forgotPassword: 'Mot de passe oublié ?',
    signIn: 'Se connecter',
    createAccount: 'Créer un compte',
    fullName: 'Nom complet',
    mainGuestName: 'Nom du client principal',
    roomNumber: 'Numéro de chambre',
    occupants: 'Occupants',
    occupant: 'Occupant',
    addOccupant: 'Ajouter un occupant',
    category: 'Catégorie',
    adult: 'Adulte',
    kid: 'Enfant',
    checkInDate: "Date d'arrivée",
    checkOutDate: 'Date de départ',
    invalidCredentials: 'E-mail ou mot de passe invalide',
    accountCreated: 'Compte créé !',

    welcomeBack: 'Bienvenue',
    happeningNow: 'EN COURS !',
    todayActivities: 'Activités du Jour',
    adultsActivities: "Activités Adultes Aujourd'hui",
    kidsActivities: "Activités Enfants Aujourd'hui",
    eveningActivities: 'Activités du Soir',
    specialEvents: 'Événements Spéciaux',
    luckyDraw: 'Tirage',
    noAdultActivities: "Aucune activité adulte prévue aujourd'hui",
    noKidsActivities: "Aucune activité enfant prévue aujourd'hui",
    noActivities: 'Aucune activité programmée',
    noEveningShows: 'Aucun spectacle de soirée programmé',
    upcomingEvents: 'Événements Spéciaux à Venir',
    myBookings: 'Mes Réservations',
    noBookings: 'Aucune réservation pour le moment',
    resortNotes: "Notes de l'Hôtel",

    activities: 'Activités',
    allActivities: 'Toutes les Activités',
    activity: 'Activité',
    location: 'Lieu',
    time: 'Heure',
    startTime: 'Heure de début',
    endTime: 'Heure de fin',
    animator: 'Animateur',
    animators: 'Animateurs',
    description: 'Description',
    capacity: 'Capacité',
    spotsLeft: 'places restantes',
    full: 'Complet',
    bookNow: 'Réserver',
    booked: 'Réservé',
    signedUp: 'Inscrit',
    cancelBooking: 'Annuler la réservation',
    bookActivity: "Réserver l'activité",
    bookingConfirmed: 'Réservation confirmée !',
    bookingCancelled: 'Réservation annulée',
    liveNow: 'EN DIRECT',
    starting: 'Démarrage',
    startsIn: 'Commence dans',
    minutes: 'minutes',
    showMore: 'Voir plus',
    showLess: 'Voir moins',

    liveScoreboard: 'Tableau des Scores en Direct',
    participants: 'participants',
    winner: 'Gagnant',
    score: 'Score',
    team: 'Équipe',
    round: 'Manche',
    total: 'Total',
    rank: 'Classement',
    tournament: 'Tournoi',
    results: 'Résultats',
    finalRanking: 'Classement Final',
    points: 'Points',

    feedback: 'Avis',
    submitFeedback: 'Donner un Avis',
    leaveFeedback: 'Laisser un avis',
    rateActivity: 'Notez cette activité',
    excellent: 'Excellent',
    good: 'Bien',
    bad: 'Mauvais',
    comment: 'Commentaire',
    feedbackThanks: 'Merci pour votre avis !',

    notifications: 'Notifications',
    noNotifications: 'Aucune notification',
    notificationSettings: 'Paramètres de Notification',
    notificationPreferences: 'Préférences de Notification',
    enablePushNotifications: 'Activer les notifications push',
    enablePushDesc: 'Si désactivé, vous ne recevrez aucune notification push. Vous pouvez réactiver à tout moment.',
    browserPermission: 'Permission du navigateur',
    browserPermissionBlocked: 'Bloqué dans les paramètres du navigateur',
    browserPermissionDesc: 'Autoriser ce navigateur à recevoir des notifications push',
    activityReminders: "Rappels d'activité",
    activityRemindersDesc: 'Soyez prévenu juste avant le début d’une activité',
    liveActivityNotifications: "Notifications d'activité en direct",
    liveActivityDesc: 'Soyez informé dès qu’une activité commence',
    generalAnnouncements: 'Annonces générales',
    generalAnnouncementsDesc: "Annonces importantes de l'équipe de l'hôtel",
    activityAssignments: "Affectations d'activité",
    activityAssignmentsDesc: 'Quand on vous attribue une nouvelle activité',
    scheduleChanges: 'Changements de planning',
    scheduleChangesDesc: "Modifications d'horaire, de lieu ou d'équipe pour vos activités",
    couldNotSavePref: "Impossible d'enregistrer la préférence. Réessayez.",
    loadingPrefs: 'Chargement des préférences…',
    backToDashboard: 'Retour au tableau de bord',

    adminDashboard: 'Tableau de Bord Admin',
    animatorDashboard: 'Tableau de Bord Animateur',
    chiefAnimator: 'Chef Animateur',
    liveMonitor: 'Suivi en Direct',
    addAnimator: 'Ajouter Animateur',
    evaluate: 'Évaluer',
    newSession: 'Nouvelle Session',
    draw: 'Tirer',
    scoreSheetsLibrary: 'Bibliothèque des Feuilles de Score',
    resultsArchive: 'Archives des Résultats',
    categoryImages: 'Images de Catégorie',
    devMaintenance: 'Développeur / Maintenance',
    dedupePushTokens: 'Dédupliquer les jetons push',
    migrateImages: 'Migrer les images',
    enablePush: 'Activer les notifications',
    testPush: 'Tester notification',
    performanceMode: 'Mode Performance',
    performanceModeOn: 'Mode Performance ACTIVÉ',
    performanceModeOff: 'Mode Performance DÉSACTIVÉ',
    showAdvancedTools: 'Afficher les outils avancés',
    hideAdvancedTools: 'Masquer les outils avancés',
    devMaintenanceHint: "Outils de maintenance — à n'utiliser qu'en dépannage.",
    totalActivities: 'activités au total',
    activeGuests: 'Clients actifs',
    pendingApprovals: 'Approbations en attente',
    sendNotification: 'Envoyer une notification',
    notificationTitle: 'Titre',
    notificationMessage: 'Message',
    audience: 'Audience',
    audienceAll: 'Personnel et Clients',
    audienceGuests: 'Clients uniquement',
    audienceStaff: 'Personnel uniquement',

    calendarPlanner: 'Planificateur',
    addActivity: 'Ajouter une activité',
    selectDate: 'Choisir une date',
    selectTime: "Choisir l'heure",
    activityName: 'Nom de l\'activité',
    saveOnly: 'Enregistrer uniquement',
    saveAndNotify: 'Enregistrer & Notifier',
    programSaved: 'Programme enregistré !',
    notifyingGuests: 'Notification des clients…',

    roomRanges: 'Plages de chambres',
    roomRangesDesc: "Configurer les plages valides pour l'inscription des clients",
    addRange: 'Ajouter une plage',
    saveRanges: 'Enregistrer les plages',
    start: 'Début',
    end: 'Fin',

    errorGeneric: "Une erreur s'est produite. Veuillez réessayer.",
    errorNetwork: 'Erreur réseau. Vérifiez votre connexion.',
    errorPermission: "Vous n'avez pas la permission de faire cela.",
    sessionExpired: 'Session expirée — veuillez vous reconnecter.',
    copied: 'Copié dans le presse-papiers',

    hotelServices: "Services de l'hôtel",
    view: 'Voir',
    liveActivitiesTitle: 'Activités en direct',
    noLiveActivitiesNow: "Aucune activité en direct pour l'instant.",
    communityMode: 'Mode Communauté',
    appInformation: "Informations de l'application",
    version: 'Version',
    activityFull: 'Activité complète',
    joinNow: 'Rejoindre',
    notesLabel: 'Notes',
    noActivitiesScheduled: 'Aucune activité prévue',
    pickLuckyTitle: 'Choisissez vos numéros chance (1-100)',
    dailyLuckyNumber: 'Numéro chance du jour',
    luckyRule1: 'Chaque personne choisit son numéro entre 1 et 100',
    luckyRule2: 'Ajoutez des entrées pour les enfants et la famille',
    luckyRule3: 'Chaque chambre peut soumettre jusqu\'à 4 entrées par jour',
    luckyRule4: "L'administration tire le numéro gagnant en fin de journée",
    luckyRule5: 'Le numéro le plus proche du tirage gagne !',
    howWasActivity: "Comment était l'activité ?",
    participantDetails: 'Détails du participant',
    nameField: 'Nom',
    tapPhotoChange: 'Touchez la photo pour la changer',
    closedToday: "Fermé aujourd'hui",
    noServicesScheduled: 'Aucun service programmé',
    menu: 'Menu',
    markAllRead: 'Tout marquer comme lu',
    tapToRead: 'TOUCHEZ POUR LIRE',
    noNotificationsYet: 'Aucune notification pour le moment',
    willNotifyYou: 'Nous vous notifierons quand quelque chose se passera',
    thisWeekProgram: 'Programme de la semaine',
    liveBadge: 'EN DIRECT',
    justNow: 'À l\'instant',
    musicStoreDescription: 'Découvrez nos chansons originales de resort, la musique de Kids Disco, les titres de Pool Party et l\'ambiance vacances.',
    exploreMusicStore: 'Explorer Music Store',
    luckyLockedTitle: 'Vos numéros chance sont verrouillés',
    luckyLockedMessage: "🎲 Le tirage est prêt ! Restez à l'écoute — l'équipe d'animation va bientôt lancer le tirage.",
    luckyTimeRemaining: 'Temps restant pour choisir vos numéros chance',
    luckyPickHint: 'Choisissez jusqu\'à 4 numéros chance (1–100) pour votre chambre',
    editNumbers: 'Modifier les numéros',
    pickNumbers: 'Choisir les numéros',
    welcomeGreeting: 'Bienvenue',
    musicStoreTitle: 'Music Store',
    musicStoreSubtitle: 'Titres originaux de notre équipe d\'animation. Gardez-les à vie.',
    exitMusicStore: 'Quitter Music Store',
    loadingMusic: 'Chargement de la musique…',
    storeOpeningSoon: 'Boutique bientôt ouverte',
    storeNewTracksMsg: 'De nouveaux titres apparaîtront ici dès que l\'équipe les publiera.',
    myPurchases: 'Mes achats',
    myPurchasesSubtitle: 'Retéléchargez à volonté les titres que vous avez achetés — sans frais supplémentaires.',
    loadingPurchases: 'Chargement de vos achats…',
    noPurchases: "Vous n'avez encore acheté aucun titre.",
    purchased: 'Acheté',
    buyBtn: 'Acheter',
    downloadBtn: 'Télécharger',
    orderPrefix: 'Commande',
    buyPrefix: 'Acheter',
    buyReviewMsg: "Vérifiez votre achat et acceptez les conditions d'usage personnel pour continuer.",
    songLabel: 'Titre',
    priceLabel: 'Prix',
    buyerLabel: 'Acheteur',
    secureCheckoutBy: 'Paiement sécurisé par',
    secureCheckoutTail: 'Vous serez redirigé pour saisir vos informations de paiement.',
    payLabel: 'Payer',
    couldNotLoadStore: "Impossible de charger Music Store. Veuillez réessayer.",
    previewUnavailable: 'Aperçu indisponible pour le moment.',
    pleaseAcceptAgreement: "Veuillez accepter l'accord d'usage personnel pour continuer.",
    couldNotStartPayment: 'Impossible de démarrer le paiement.',
    personalUseAgreementText: "J'accepte que ce titre soit acheté pour un usage personnel uniquement. Je ne le mettrai pas en ligne, ne le revendrai pas, ne le redistribuerai pas, ne le partagerai pas publiquement et ne l'utiliserai pas à des fins commerciales sans autorisation écrite de IC FunFlow.",
    luckyInstructions: 'Chaque personne choisit son propre numéro chance. Ajoutez des entrées pour votre famille !',
    deadlineLabel: 'Date limite',
    deadlineClosed: '(FERMÉ)',
    yourRoomsEntriesToday: "Entrées de votre chambre aujourd'hui :",
    roomLabel: 'Chambre',
    saveBtn: 'Enregistrer',
    editBtn: 'Modifier',
    submissionsClosed: "Soumissions fermées pour aujourd'hui !",
    submissionsClosedMsg: 'La date limite était {time}. Bonne chance avec vos entrées existantes !',
    allLuckySubmitted: 'Tous les numéros chance sont enregistrés !',
    goodLuckWinners: 'Bonne chance ! Les gagnants seront annoncés en fin de journée.',
    editUntilMsg: "Vous pouvez encore modifier vos numéros ci-dessus jusqu'à {time}.",
    roomEntriesToday: "Entrées de la chambre aujourd'hui",
    entriesRemaining: 'entrée(s) restante(s) pour votre chambre',
    roomLimitReached: 'Votre chambre a atteint la limite quotidienne de 4 entrées',
    newEntryPrefix: 'Nouvelle entrée',
    luckyNumberLabel: 'Numéro chance (1-100)',
    addAnotherParticipant: 'Ajouter un autre participant',
    submitLuckyOne: 'Envoyer le numéro chance',
    submitLuckyMany: 'Envoyer {n} numéros chance',
    todaysLuckyNumber: "Numéro chance du jour",
    winningNumber: 'Numéro gagnant',
    congratulationsPrefix: 'Félicitations',
    installTitle: 'Installez IC FunFlow pour une meilleure expérience',
    installBtn: 'Installer',
    installLater: 'Plus tard',
    iosInstallHint: 'Appuyez sur Partager → « Sur l\'écran d\'accueil »',
    howItWorks: 'Comment ça marche :',
    luckyRuleEditUntil: "Vous pouvez modifier vos numéros jusqu'à {time}",
    toastPushEnabled: 'Notifications push activées !',
    toastPushBlocked: 'Notifications bloquées. Veuillez les activer dans les paramètres du navigateur.',
    toastPushDisabled: 'Notifications push désactivées',
    toastSavePrefFailed: "Échec de l'enregistrement de la préférence",
    toastSignupCancelled: 'Inscription annulée',
    toastPleaseRate: 'Veuillez sélectionner une note',
    toastFeedbackThanks: 'Merci pour votre retour !',
    toastFeedbackFailed: "Échec de l'envoi du retour",
    toastInvalidLucky: 'Veuillez saisir un numéro valide (1-100)',
    toastLuckyUpdated: 'Numéro chance mis à jour !',
    toastLuckyCancelled: 'Entrée du numéro chance annulée',
    addToHomeScreenHint: "Ajoutez à l'écran d'accueil pour une meilleure expérience",
    awayModeMessage: "Pour commencer un nouveau séjour, veuillez vous adresser à notre équipe d'animation.",
    todayBadge: "Aujourd'hui",
    commentsPlaceholder: 'Un commentaire ? (optionnel)',
    menuViewer: 'Visionneuse du menu',
    notificationDetails: 'Détails de la notification',
    catAdults: 'Adultes',
    catKids: 'Enfants',
    catAllAges: 'Tous âges',
    catEveningAdults: 'Soirée adultes',
    catEveningKids: 'Soirée enfants',
    catKidsClub: 'Kids Club',
    catSports: 'Sports',
    catDj: 'DJ',
    // Welcome page (added Feb 2026 — first-visit language selector)
    resortEntertainment: "Animation de l'hôtel",
    welcomeHeroLine1: 'Votre séjour,',
    welcomeHeroLine2: 'amplifié.',
    welcomeHeroSubtitle: "Connectez-vous à votre compte chambre, créez-en un pour votre séjour, ou jetez simplement un œil — aucun compte requis.",
    welcomeChipDaily: 'Programme du jour',
    welcomeChipEvents: 'Événements spéciaux',
    welcomeChipMusic: 'Music store',
    welcomeSigninDesc: "Client fidèle ou membre du personnel.",
    welcomeCreateDesc: "Première fois ? Créez votre compte pour le séjour.",
    welcomeExploreTitle: 'Explorer en visiteur',
    welcomeExploreDesc: "Aucune inscription requise. Parcourez programme, événements et boutique.",
    welcomeFooterHint: "Réservations, avis et numéro chance nécessitent un compte. Les achats de musique fonctionnent aussi en visiteur.",
    // Auth page (added Feb 2026 — carry language across login/register)
    signingIn: 'Connexion en cours…',
    creatingAccount: 'Création du compte…',
    joinUs: 'Rejoignez-nous !',
    createAccountSubtitle: 'Créez votre compte pour commencer',
    iAmA: 'Je suis :',
    guest: 'Client',
    animatorRole: 'Animateur',
    // Explore as Visitor (added Feb 2026 — carry Welcome language into /explore)
    exploreBrand: 'IC FunFlow — Explorer',
    exploreBackWelcome: 'Accueil',
    exploreJoin: 'Rejoindre',
    exploreTabProgramme: 'Programme',
    exploreTabFoodBeverage: 'Restauration',
    exploreTabEvents: 'Événements Spéciaux',
    exploreTabStore: 'Music Store',
    exploreTabNews: 'Actualités',
    exploreTodaysProgramme: "Programme d'aujourd'hui",
    exploreLoading: 'Chargement…',
    exploreSelectTitle: 'Quel hôtel souhaitez-vous explorer ?',
    exploreSelectSubtitle: 'Choisissez un hôtel',
    exploreSelectDesc: 'Programme, restauration, événements et musique sont personnalisés par établissement. Choisissez-en un pour voir ce qui se passe maintenant.',
    exploreSelectLoading: 'Chargement des hôtels…',
    exploreSelectLoadError: 'Impossible de charger les hôtels pour le moment.',
    exploreSelectEmpty: 'Aucun hôtel actif pour le moment. Merci de réessayer plus tard.',
    exploreSelectContinue: 'Continuer',
    exploreSelectStaleHotel: "Cet hôtel n'est plus disponible — choisissez-en un autre.",
    exploreChangeHotel: 'Changer',
    exploreDisabledTitle: "L'exploration n'est pas disponible dans cet hôtel",
    exploreDisabledDesc: 'Contactez la réception pour le programme et les services actuels.',
    exploreNoSectionsTitle: 'Aucune section à afficher',
    exploreNoSectionsDesc: "Cet hôtel n'a encore publié aucun contenu visiteur.",
    exploreLoadingStore: 'Chargement de la boutique…',
    exploreLoadingEvents: 'Chargement des événements…',
    exploreLoadingFB: 'Chargement de la restauration…',
    exploreLoadingUpdates: 'Chargement des actualités…',
    exploreNothingOnTodayTitle: "Rien de prévu aujourd'hui",
    exploreNothingOnTodayDesc: "Le programme du jour n'a pas encore été publié. Revenez plus tard ou consultez notre équipe d'animation pour les activités du jour.",
    exploreUnlockWeeklyTitle: 'Créez un compte pour débloquer tout le programme de la semaine.',
    exploreUnlockWeeklyDesc: 'Consultez chaque activité des 14 prochains jours et réservez directement depuis votre tableau de bord.',
    exploreStoreEmptyTitle: 'Boutique bientôt ouverte',
    exploreStoreEmptyDesc: "Aucun titre disponible pour le moment. Revenez plus tard.",
    exploreEventsEmptyTitle: 'Aucun événement spécial programmé',
    exploreEventsEmptyDesc: "Les spectacles et soirées à thème apparaîtront ici dès leur publication par l'équipe.",
    exploreEventBadge: 'Événement spécial',
    exploreFBComingSoonTitle: 'Bientôt disponible',
    exploreFBComingSoonDesc: 'Les informations sur la restauration apparaîtront ici dès leur publication.',
    exploreFBNothingPublished: 'Rien de publié pour le moment — revenez bientôt.',
    exploreFBNoInfo: 'Aucune information publiée pour le moment.',
    exploreNewsEmptyTitle: 'Rien de nouveau pour le moment',
    exploreNewsEmptyDesc: "Quand l'équipe envoie une annonce, elle apparaîtra ici.",
    exploreNewsUpdateBadge: 'Actualité',
    // Food & Beverage card labels (shared DepartmentItemCard — Feb 2026)
    fbClosed: 'Fermé',
    fbClosedNow: 'Fermé maintenant',
    fbOpenNow: 'Ouvert maintenant',
    fbOpensAt: 'Ouvre à',
    fbViewWeeklySchedule: "Voir l'horaire de la semaine",
    fbHideWeeklySchedule: "Masquer l'horaire de la semaine",
    fbViewMenu: 'Voir le menu',
    fbMenus: 'Menus',
    fbMenu: 'Menu',
    fbWebsite: 'Site web',
    fbMenuLink: 'Lien du menu',
    fbDressCode: 'Tenue',
    fbService: 'Service',
  },

  // ─────────────────────────────────────────────────────────────────────
  // ESPAÑOL
  // ─────────────────────────────────────────────────────────────────────
  es: {
    home: 'Inicio',
    today: 'Hoy',
    weekly: 'Programa Semanal',
    week: 'Semana',
    profile: 'Perfil',
    bookings: 'Reservas',
    logout: 'Cerrar Sesión',
    save: 'Guardar',
    saveChanges: 'Guardar cambios',
    cancel: 'Cancelar',
    close: 'Cerrar',
    delete: 'Eliminar',
    edit: 'Editar',
    add: 'Añadir',
    remove: 'Quitar',
    loading: 'Cargando…',
    search: 'Buscar',
    searchPlaceholder: 'Buscar por nombre, habitación…',
    noResults: 'Sin resultados',
    back: 'Volver',
    done: 'Hecho',
    next: 'Siguiente',
    previous: 'Anterior',
    submit: 'Enviar',
    confirm: 'Confirmar',
    yes: 'Sí',
    no: 'No',
    refresh: 'Actualizar',
    retry: 'Reintentar',
    saving: 'Guardando…',
    saved: 'Guardado',
    settings: 'Ajustes',
    language: 'Idioma',

    welcomeTo: 'Bienvenido a',
    signInSubtitle: 'Inicia sesión para descubrir el programa de hoy',
    login: 'Acceder',
    register: 'Registrarse',
    email: 'Correo',
    password: 'Contraseña',
    forgotPassword: '¿Olvidó su contraseña?',
    signIn: 'Iniciar Sesión',
    createAccount: 'Crear Cuenta',
    fullName: 'Nombre completo',
    mainGuestName: 'Huésped principal',
    roomNumber: 'Número de habitación',
    occupants: 'Ocupantes',
    occupant: 'Ocupante',
    addOccupant: 'Añadir ocupante',
    category: 'Categoría',
    adult: 'Adulto',
    kid: 'Niño',
    checkInDate: 'Fecha de entrada',
    checkOutDate: 'Fecha de salida',
    invalidCredentials: 'Correo o contraseña incorrectos',
    accountCreated: '¡Cuenta creada!',

    welcomeBack: 'Bienvenido de nuevo',
    happeningNow: '¡EN MARCHA AHORA!',
    todayActivities: 'Actividades de Hoy',
    adultsActivities: 'Actividades para Adultos Hoy',
    kidsActivities: 'Actividades para Niños Hoy',
    eveningActivities: 'Actividades de la Noche',
    specialEvents: 'Eventos Especiales',
    luckyDraw: 'Sorteo',
    noAdultActivities: 'No hay actividades para adultos programadas hoy',
    noKidsActivities: 'No hay actividades para niños programadas hoy',
    noActivities: 'No hay actividades programadas',
    noEveningShows: 'No hay espectáculos nocturnos programados',
    upcomingEvents: 'Próximos Eventos Especiales',
    myBookings: 'Mis Reservas',
    noBookings: 'Sin reservas todavía',
    resortNotes: 'Notas del Hotel',

    activities: 'Actividades',
    allActivities: 'Todas las Actividades',
    activity: 'Actividad',
    location: 'Ubicación',
    time: 'Hora',
    startTime: 'Hora de inicio',
    endTime: 'Hora de fin',
    animator: 'Animador',
    animators: 'Animadores',
    description: 'Descripción',
    capacity: 'Capacidad',
    spotsLeft: 'plazas disponibles',
    full: 'Completo',
    bookNow: 'Reservar',
    booked: 'Reservado',
    signedUp: 'Inscrito',
    cancelBooking: 'Cancelar reserva',
    bookActivity: 'Reservar Actividad',
    bookingConfirmed: '¡Reserva confirmada!',
    bookingCancelled: 'Reserva cancelada',
    liveNow: 'EN VIVO',
    starting: 'Comenzando',
    startsIn: 'Comienza en',
    minutes: 'minutos',
    showMore: 'Ver más',
    showLess: 'Ver menos',

    liveScoreboard: 'Marcador en Vivo',
    participants: 'participantes',
    winner: 'Ganador',
    score: 'Puntuación',
    team: 'Equipo',
    round: 'Ronda',
    total: 'Total',
    rank: 'Clasificación',
    tournament: 'Torneo',
    results: 'Resultados',
    finalRanking: 'Clasificación Final',
    points: 'Puntos',

    feedback: 'Opinión',
    submitFeedback: 'Enviar Opinión',
    leaveFeedback: 'Dejar una opinión',
    rateActivity: 'Califica esta actividad',
    excellent: 'Excelente',
    good: 'Bueno',
    bad: 'Malo',
    comment: 'Comentario',
    feedbackThanks: '¡Gracias por tu opinión!',

    notifications: 'Notificaciones',
    noNotifications: 'Sin notificaciones',
    notificationSettings: 'Ajustes de Notificación',
    notificationPreferences: 'Preferencias de Notificación',
    enablePushNotifications: 'Activar notificaciones push',
    enablePushDesc: 'Si está desactivado, no recibirás ninguna notificación push. Puedes reactivarlo en cualquier momento.',
    browserPermission: 'Permiso del navegador',
    browserPermissionBlocked: 'Bloqueado en los ajustes del navegador',
    browserPermissionDesc: 'Permitir a este navegador recibir notificaciones push',
    activityReminders: 'Recordatorios de actividad',
    activityRemindersDesc: 'Recibe un aviso justo antes del inicio de la actividad',
    liveActivityNotifications: 'Notificaciones de actividad en vivo',
    liveActivityDesc: 'Recibe aviso cuando una actividad comience',
    generalAnnouncements: 'Anuncios generales',
    generalAnnouncementsDesc: 'Anuncios importantes del equipo del hotel',
    activityAssignments: 'Asignaciones de actividad',
    activityAssignmentsDesc: 'Cuando se te asigna una nueva actividad',
    scheduleChanges: 'Cambios de horario',
    scheduleChangesDesc: 'Cambios de hora, lugar o equipo en tus actividades',
    couldNotSavePref: 'No se pudo guardar la preferencia. Inténtalo de nuevo.',
    loadingPrefs: 'Cargando preferencias…',
    backToDashboard: 'Volver al panel',

    adminDashboard: 'Panel de Administrador',
    animatorDashboard: 'Panel del Animador',
    chiefAnimator: 'Jefe de Animación',
    liveMonitor: 'Monitor en Vivo',
    addAnimator: 'Añadir animador',
    evaluate: 'Evaluar',
    newSession: 'Nueva Sesión',
    draw: 'Sortear',
    scoreSheetsLibrary: 'Biblioteca de Hojas de Puntuación',
    resultsArchive: 'Archivo de Resultados',
    categoryImages: 'Imágenes de Categoría',
    devMaintenance: 'Desarrollador / Mantenimiento',
    dedupePushTokens: 'Deduplicar tokens push',
    migrateImages: 'Migrar imágenes',
    enablePush: 'Activar Push',
    testPush: 'Probar Push',
    performanceMode: 'Modo Rendimiento',
    performanceModeOn: 'Modo Rendimiento ACTIVADO',
    performanceModeOff: 'Modo Rendimiento DESACTIVADO',
    showAdvancedTools: 'Mostrar herramientas avanzadas',
    hideAdvancedTools: 'Ocultar herramientas avanzadas',
    devMaintenanceHint: 'Herramientas de mantenimiento — usar solo para resolver problemas.',
    totalActivities: 'actividades totales',
    activeGuests: 'Huéspedes activos',
    pendingApprovals: 'Aprobaciones pendientes',
    sendNotification: 'Enviar notificación',
    notificationTitle: 'Título',
    notificationMessage: 'Mensaje',
    audience: 'Audiencia',
    audienceAll: 'Personal y Huéspedes',
    audienceGuests: 'Solo huéspedes',
    audienceStaff: 'Solo personal',

    calendarPlanner: 'Planificador',
    addActivity: 'Añadir actividad',
    selectDate: 'Selecciona fecha',
    selectTime: 'Selecciona hora',
    activityName: 'Nombre de la actividad',
    saveOnly: 'Solo guardar',
    saveAndNotify: 'Guardar y Notificar',
    programSaved: '¡Programa guardado!',
    notifyingGuests: 'Notificando a los huéspedes…',

    roomRanges: 'Rangos de habitaciones',
    roomRangesDesc: 'Configurar los rangos válidos para el registro de huéspedes',
    addRange: 'Añadir rango',
    saveRanges: 'Guardar rangos',
    start: 'Inicio',
    end: 'Fin',

    errorGeneric: 'Algo salió mal. Inténtalo de nuevo.',
    errorNetwork: 'Error de red. Comprueba tu conexión.',
    errorPermission: 'No tienes permiso para hacer eso.',
    sessionExpired: 'Sesión caducada — vuelve a iniciar sesión.',
    copied: 'Copiado al portapapeles',

    hotelServices: 'Servicios del hotel',
    view: 'Ver',
    liveActivitiesTitle: 'Actividades en vivo',
    noLiveActivitiesNow: 'No hay actividades en vivo por ahora.',
    communityMode: 'Modo Comunidad',
    appInformation: 'Información de la app',
    version: 'Versión',
    activityFull: 'Actividad completa',
    joinNow: 'Unirse ahora',
    notesLabel: 'Notas',
    noActivitiesScheduled: 'No hay actividades programadas',
    pickLuckyTitle: 'Elige tus números de la suerte (1-100)',
    dailyLuckyNumber: 'Número de la suerte del día',
    luckyRule1: 'Cada persona elige su número del 1 al 100',
    luckyRule2: 'Añade entradas para niños y familiares',
    luckyRule3: 'Cada habitación puede enviar hasta 4 entradas por día',
    luckyRule4: 'La administración sortea el número ganador al final del día',
    luckyRule5: '¡El número más cercano al sorteado gana!',
    howWasActivity: '¿Qué te pareció la actividad?',
    participantDetails: 'Datos del participante',
    nameField: 'Nombre',
    tapPhotoChange: 'Toca la foto para cambiarla',
    closedToday: 'Cerrado hoy',
    noServicesScheduled: 'Sin servicios programados',
    menu: 'Menú',
    markAllRead: 'Marcar todo como leído',
    tapToRead: 'TOCA PARA LEER',
    noNotificationsYet: 'Aún no hay notificaciones',
    willNotifyYou: 'Te avisaremos cuando pase algo',
    thisWeekProgram: 'Programa de la semana',
    liveBadge: 'EN VIVO',
    justNow: 'Justo ahora',
    musicStoreDescription: 'Descubre nuestras canciones originales del resort, música para Kids Disco, temas de Pool Party y ambiente vacacional.',
    exploreMusicStore: 'Explorar Music Store',
    luckyLockedTitle: 'Tus números de la suerte están bloqueados',
    luckyLockedMessage: '🎲 ¡El sorteo está listo! Atento — el equipo de animación comenzará el sorteo en breve.',
    luckyTimeRemaining: 'Tiempo restante para elegir tus números de la suerte',
    luckyPickHint: 'Elige hasta 4 números de la suerte (1–100) para tu habitación',
    editNumbers: 'Editar números',
    pickNumbers: 'Elegir números',
    welcomeGreeting: 'Bienvenido',
    musicStoreTitle: 'Music Store',
    musicStoreSubtitle: 'Temas originales de nuestro equipo de animación. Guárdalos para siempre.',
    exitMusicStore: 'Salir de Music Store',
    loadingMusic: 'Cargando música…',
    storeOpeningSoon: 'Tienda próximamente disponible',
    storeNewTracksMsg: 'Nuevos temas aparecerán aquí a medida que el equipo los publique.',
    myPurchases: 'Mis compras',
    myPurchasesSubtitle: 'Vuelve a descargar cualquier tema comprado — sin coste adicional.',
    loadingPurchases: 'Cargando tus compras…',
    noPurchases: 'Aún no has comprado ningún tema.',
    purchased: 'Comprado',
    buyBtn: 'Comprar',
    downloadBtn: 'Descargar',
    orderPrefix: 'Pedido',
    buyPrefix: 'Comprar',
    buyReviewMsg: 'Revisa tu compra y acepta las condiciones de uso personal para continuar.',
    songLabel: 'Tema',
    priceLabel: 'Precio',
    buyerLabel: 'Comprador',
    secureCheckoutBy: 'Pago seguro por',
    secureCheckoutTail: 'Serás redirigido para introducir tus datos de pago.',
    payLabel: 'Pagar',
    couldNotLoadStore: 'No se pudo cargar Music Store. Inténtalo de nuevo.',
    previewUnavailable: 'Vista previa no disponible ahora.',
    pleaseAcceptAgreement: 'Por favor, acepta el acuerdo de uso personal para continuar.',
    couldNotStartPayment: 'No se pudo iniciar el pago.',
    personalUseAgreementText: 'Acepto que este tema se compra únicamente para uso personal. No lo subiré, revenderé, redistribuiré, compartiré públicamente ni lo utilizaré comercialmente sin permiso escrito de IC FunFlow.',
    luckyInstructions: '¡Cada persona elige su propio número de la suerte. Añade entradas para la familia!',
    deadlineLabel: 'Fecha límite',
    deadlineClosed: '(CERRADO)',
    yourRoomsEntriesToday: 'Entradas de tu habitación hoy:',
    roomLabel: 'Habitación',
    saveBtn: 'Guardar',
    editBtn: 'Editar',
    submissionsClosed: '¡Envíos cerrados por hoy!',
    submissionsClosedMsg: 'La fecha límite era {time}. ¡Suerte con tus entradas existentes!',
    allLuckySubmitted: '¡Todos los números de la suerte enviados!',
    goodLuckWinners: '¡Suerte! Los ganadores se anunciarán al final del día.',
    editUntilMsg: 'Aún puedes editar tus números arriba hasta las {time}.',
    roomEntriesToday: 'Entradas de la habitación hoy',
    entriesRemaining: 'entrada(s) restante(s) para tu habitación',
    roomLimitReached: 'Tu habitación ha alcanzado el límite diario de 4 entradas',
    newEntryPrefix: 'Nueva entrada',
    luckyNumberLabel: 'Número de la suerte (1-100)',
    addAnotherParticipant: 'Añadir otro participante',
    submitLuckyOne: 'Enviar número de la suerte',
    submitLuckyMany: 'Enviar {n} números de la suerte',
    todaysLuckyNumber: 'Número de la suerte de hoy',
    winningNumber: 'Número ganador',
    congratulationsPrefix: 'Felicidades',
    installTitle: 'Instala IC FunFlow para una mejor experiencia',
    installBtn: 'Instalar',
    installLater: 'Más tarde',
    iosInstallHint: 'Toca Compartir → «Añadir a pantalla de inicio»',
    howItWorks: '¿Cómo funciona?:',
    luckyRuleEditUntil: 'Puedes editar tus números hasta las {time}',
    toastPushEnabled: '¡Notificaciones push activadas!',
    toastPushBlocked: 'Notificaciones bloqueadas. Actívalas en los ajustes del navegador.',
    toastPushDisabled: 'Notificaciones push desactivadas',
    toastSavePrefFailed: 'Error al guardar la preferencia',
    toastSignupCancelled: 'Inscripción cancelada',
    toastPleaseRate: 'Por favor, selecciona una valoración',
    toastFeedbackThanks: '¡Gracias por tu opinión!',
    toastFeedbackFailed: 'Error al enviar la opinión',
    toastInvalidLucky: 'Por favor, introduce un número válido (1-100)',
    toastLuckyUpdated: '¡Número de la suerte actualizado!',
    toastLuckyCancelled: 'Entrada del número de la suerte cancelada',
    addToHomeScreenHint: 'Añade a la pantalla de inicio para una mejor experiencia',
    awayModeMessage: 'Para empezar unas nuevas vacaciones, visita a nuestro equipo de animación.',
    todayBadge: 'Hoy',
    commentsPlaceholder: '¿Algún comentario? (opcional)',
    menuViewer: 'Visor del menú',
    notificationDetails: 'Detalles de la notificación',
    catAdults: 'Adultos',
    catKids: 'Niños',
    catAllAges: 'Todas las edades',
    catEveningAdults: 'Noche adultos',
    catEveningKids: 'Noche niños',
    catKidsClub: 'Kids Club',
    catSports: 'Deportes',
    catDj: 'DJ',
    // Welcome page (added Feb 2026 — first-visit language selector)
    resortEntertainment: 'Animación de hotel',
    welcomeHeroLine1: 'Tu estancia,',
    welcomeHeroLine2: 'amplificada.',
    welcomeHeroSubtitle: 'Inicia sesión en tu cuenta de habitación, crea una para tu estancia o simplemente echa un vistazo — no necesitas cuenta.',
    welcomeChipDaily: 'Programa diario',
    welcomeChipEvents: 'Eventos especiales',
    welcomeChipMusic: 'Music store',
    welcomeSigninDesc: 'Huésped que regresa o miembro del equipo.',
    welcomeCreateDesc: '¿Primera vez? Configura tu cuenta de estancia.',
    welcomeExploreTitle: 'Explorar como visitante',
    welcomeExploreDesc: 'Sin registro. Explora el programa, los eventos y la tienda.',
    welcomeFooterHint: 'Reservas, opiniones y número de la suerte requieren una cuenta. Las compras de música funcionan también como visitante.',
    // Auth page (added Feb 2026 — carry language across login/register)
    signingIn: 'Iniciando sesión…',
    creatingAccount: 'Creando cuenta…',
    joinUs: '¡Únete a nosotros!',
    createAccountSubtitle: 'Crea tu cuenta para empezar',
    iAmA: 'Soy:',
    guest: 'Huésped',
    animatorRole: 'Animador',
    // Explore as Visitor (added Feb 2026 — carry Welcome language into /explore)
    exploreBrand: 'IC FunFlow — Explorar',
    exploreBackWelcome: 'Inicio',
    exploreJoin: 'Unirse',
    exploreTabProgramme: 'Programa',
    exploreTabFoodBeverage: 'Restauración',
    exploreTabEvents: 'Eventos Especiales',
    exploreTabStore: 'Music Store',
    exploreTabNews: 'Novedades',
    exploreTodaysProgramme: 'Programa de Hoy',
    exploreLoading: 'Cargando…',
    exploreSelectTitle: '¿Qué hotel quieres explorar?',
    exploreSelectSubtitle: 'Elige un hotel',
    exploreSelectDesc: 'El programa, la restauración, los eventos y la música se personalizan por establecimiento. Elige uno para ver lo que hay ahora.',
    exploreSelectLoading: 'Cargando hoteles…',
    exploreSelectLoadError: 'No se pudieron cargar los hoteles ahora.',
    exploreSelectEmpty: 'No hay hoteles activos por ahora. Vuelve más tarde.',
    exploreSelectContinue: 'Continuar',
    exploreSelectStaleHotel: 'Ese hotel ya no está disponible — elige otro.',
    exploreChangeHotel: 'Cambiar',
    exploreDisabledTitle: 'La exploración no está disponible en este hotel',
    exploreDisabledDesc: 'Contacta con recepción para el programa y los servicios actuales.',
    exploreNoSectionsTitle: 'No hay secciones para mostrar',
    exploreNoSectionsDesc: 'Este hotel aún no ha publicado contenido para visitantes.',
    exploreLoadingStore: 'Cargando la tienda…',
    exploreLoadingEvents: 'Cargando eventos…',
    exploreLoadingFB: 'Cargando restauración…',
    exploreLoadingUpdates: 'Cargando novedades…',
    exploreNothingOnTodayTitle: 'Nada programado para hoy',
    exploreNothingOnTodayDesc: 'El programa de hoy aún no se ha publicado. Vuelva a comprobarlo más tarde o visite a nuestro equipo de animación.',
    exploreUnlockWeeklyTitle: 'Crea una cuenta para desbloquear el programa semanal completo.',
    exploreUnlockWeeklyDesc: 'Consulta cada actividad de los próximos 14 días y reserva directamente desde tu panel.',
    exploreStoreEmptyTitle: 'Tienda próximamente',
    exploreStoreEmptyDesc: 'Aún no hay temas disponibles. Vuelve más tarde.',
    exploreEventsEmptyTitle: 'No hay eventos especiales programados',
    exploreEventsEmptyDesc: 'Los espectáculos y noches temáticas aparecerán aquí en cuanto el equipo los publique.',
    exploreEventBadge: 'Evento Especial',
    exploreFBComingSoonTitle: 'Próximamente',
    exploreFBComingSoonDesc: 'La información de restauración aparecerá aquí a medida que el hotel la publique.',
    exploreFBNothingPublished: 'Nada publicado aún — vuelve pronto.',
    exploreFBNoInfo: 'Aún no hay información publicada.',
    exploreNewsEmptyTitle: 'Nada nuevo por ahora',
    exploreNewsEmptyDesc: 'Cuando el equipo envíe un anuncio, aparecerá aquí.',
    exploreNewsUpdateBadge: 'Actualización',
    // Food & Beverage card labels (shared DepartmentItemCard — Feb 2026)
    fbClosed: 'Cerrado',
    fbClosedNow: 'Cerrado ahora',
    fbOpenNow: 'Abierto ahora',
    fbOpensAt: 'Abre a las',
    fbViewWeeklySchedule: 'Ver horario semanal',
    fbHideWeeklySchedule: 'Ocultar horario semanal',
    fbViewMenu: 'Ver menú',
    fbMenus: 'Menús',
    fbMenu: 'Menú',
    fbWebsite: 'Sitio web',
    fbMenuLink: 'Enlace del menú',
    fbDressCode: 'Código de vestimenta',
    fbService: 'Servicio',
  },

  // ─────────────────────────────────────────────────────────────────────
  // DEUTSCH
  // ─────────────────────────────────────────────────────────────────────
  de: {
    home: 'Startseite',
    today: 'Heute',
    weekly: 'Wochenprogramm',
    week: 'Woche',
    profile: 'Profil',
    bookings: 'Buchungen',
    logout: 'Abmelden',
    save: 'Speichern',
    saveChanges: 'Änderungen speichern',
    cancel: 'Abbrechen',
    close: 'Schließen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    add: 'Hinzufügen',
    remove: 'Entfernen',
    loading: 'Lädt…',
    search: 'Suchen',
    searchPlaceholder: 'Suche nach Name, Zimmer…',
    noResults: 'Keine Ergebnisse',
    back: 'Zurück',
    done: 'Fertig',
    next: 'Weiter',
    previous: 'Zurück',
    submit: 'Senden',
    confirm: 'Bestätigen',
    yes: 'Ja',
    no: 'Nein',
    refresh: 'Aktualisieren',
    retry: 'Wiederholen',
    saving: 'Speichern…',
    saved: 'Gespeichert',
    settings: 'Einstellungen',
    language: 'Sprache',

    welcomeTo: 'Willkommen bei',
    signInSubtitle: 'Melden Sie sich an und entdecken Sie das heutige Programm',
    login: 'Anmelden',
    register: 'Registrieren',
    email: 'E-Mail',
    password: 'Passwort',
    forgotPassword: 'Passwort vergessen?',
    signIn: 'Anmelden',
    createAccount: 'Konto erstellen',
    fullName: 'Vollständiger Name',
    mainGuestName: 'Hauptgast',
    roomNumber: 'Zimmernummer',
    occupants: 'Gäste',
    occupant: 'Gast',
    addOccupant: 'Gast hinzufügen',
    category: 'Kategorie',
    adult: 'Erwachsener',
    kid: 'Kind',
    checkInDate: 'Anreisedatum',
    checkOutDate: 'Abreisedatum',
    invalidCredentials: 'Ungültige E-Mail oder Passwort',
    accountCreated: 'Konto erstellt!',

    welcomeBack: 'Willkommen zurück',
    happeningNow: 'JETZT LIVE!',
    todayActivities: 'Heutige Aktivitäten',
    adultsActivities: 'Erwachsenen-Aktivitäten Heute',
    kidsActivities: 'Kinder-Aktivitäten Heute',
    eveningActivities: 'Abendaktivitäten',
    specialEvents: 'Besondere Veranstaltungen',
    luckyDraw: 'Verlosung',
    noAdultActivities: 'Keine Erwachsenen-Aktivitäten für heute geplant',
    noKidsActivities: 'Keine Kinder-Aktivitäten für heute geplant',
    noActivities: 'Keine Aktivitäten geplant',
    noEveningShows: 'Keine Abendshows geplant',
    upcomingEvents: 'Kommende besondere Veranstaltungen',
    myBookings: 'Meine Buchungen',
    noBookings: 'Noch keine Buchungen',
    resortNotes: 'Hotelnotizen',

    activities: 'Aktivitäten',
    allActivities: 'Alle Aktivitäten',
    activity: 'Aktivität',
    location: 'Ort',
    time: 'Zeit',
    startTime: 'Startzeit',
    endTime: 'Endzeit',
    animator: 'Animator',
    animators: 'Animatoren',
    description: 'Beschreibung',
    capacity: 'Kapazität',
    spotsLeft: 'Plätze frei',
    full: 'Voll',
    bookNow: 'Jetzt Buchen',
    booked: 'Gebucht',
    signedUp: 'Angemeldet',
    cancelBooking: 'Buchung stornieren',
    bookActivity: 'Aktivität Buchen',
    bookingConfirmed: 'Buchung bestätigt!',
    bookingCancelled: 'Buchung storniert',
    liveNow: 'LIVE JETZT',
    starting: 'Beginnt',
    startsIn: 'Beginnt in',
    minutes: 'Minuten',
    showMore: 'Mehr anzeigen',
    showLess: 'Weniger anzeigen',

    liveScoreboard: 'Live-Anzeigetafel',
    participants: 'Teilnehmer',
    winner: 'Gewinner',
    score: 'Punktzahl',
    team: 'Team',
    round: 'Runde',
    total: 'Gesamt',
    rank: 'Rang',
    tournament: 'Turnier',
    results: 'Ergebnisse',
    finalRanking: 'Endwertung',
    points: 'Punkte',

    feedback: 'Feedback',
    submitFeedback: 'Feedback Senden',
    leaveFeedback: 'Feedback abgeben',
    rateActivity: 'Bewerten Sie diese Aktivität',
    excellent: 'Ausgezeichnet',
    good: 'Gut',
    bad: 'Schlecht',
    comment: 'Kommentar',
    feedbackThanks: 'Vielen Dank für Ihr Feedback!',

    notifications: 'Benachrichtigungen',
    noNotifications: 'Keine Benachrichtigungen',
    notificationSettings: 'Benachrichtigungseinstellungen',
    notificationPreferences: 'Benachrichtigungseinstellungen',
    enablePushNotifications: 'Push-Benachrichtigungen aktivieren',
    enablePushDesc: 'Wenn deaktiviert, erhalten Sie keine Push-Benachrichtigungen. Sie können dies jederzeit wieder einschalten.',
    browserPermission: 'Browser-Berechtigung',
    browserPermissionBlocked: 'In Browser-Einstellungen blockiert',
    browserPermissionDesc: 'Diesem Browser erlauben, Push-Benachrichtigungen zu empfangen',
    activityReminders: 'Aktivitäts-Erinnerungen',
    activityRemindersDesc: 'Bekommen Sie eine Erinnerung kurz vor Beginn einer Aktivität',
    liveActivityNotifications: 'Live-Aktivitäts-Benachrichtigungen',
    liveActivityDesc: 'Werden Sie benachrichtigt, sobald eine Aktivität startet',
    generalAnnouncements: 'Allgemeine Ankündigungen',
    generalAnnouncementsDesc: 'Wichtige Ankündigungen vom Hotel-Team',
    activityAssignments: 'Aktivitäts-Zuweisungen',
    activityAssignmentsDesc: 'Wenn Ihnen eine neue Aktivität zugewiesen wird',
    scheduleChanges: 'Planänderungen',
    scheduleChangesDesc: 'Änderungen an Zeit, Ort oder Team für Ihre Aktivitäten',
    couldNotSavePref: 'Einstellung konnte nicht gespeichert werden. Bitte erneut versuchen.',
    loadingPrefs: 'Einstellungen werden geladen…',
    backToDashboard: 'Zurück zum Dashboard',

    adminDashboard: 'Admin-Dashboard',
    animatorDashboard: 'Animator-Dashboard',
    chiefAnimator: 'Chef-Animator',
    liveMonitor: 'Live-Überwachung',
    addAnimator: 'Animator hinzufügen',
    evaluate: 'Bewerten',
    newSession: 'Neue Sitzung',
    draw: 'Ziehen',
    scoreSheetsLibrary: 'Wertungsbogen-Bibliothek',
    resultsArchive: 'Ergebnisarchiv',
    categoryImages: 'Kategoriebilder',
    devMaintenance: 'Entwickler / Wartung',
    dedupePushTokens: 'Push-Token deduplizieren',
    migrateImages: 'Bilder migrieren',
    enablePush: 'Push aktivieren',
    testPush: 'Push testen',
    performanceMode: 'Leistungsmodus',
    performanceModeOn: 'Leistungsmodus EIN',
    performanceModeOff: 'Leistungsmodus AUS',
    showAdvancedTools: 'Erweiterte Werkzeuge anzeigen',
    hideAdvancedTools: 'Erweiterte Werkzeuge verbergen',
    devMaintenanceHint: 'Wartungswerkzeuge — nur bei Fehlerbehebung verwenden.',
    totalActivities: 'Aktivitäten gesamt',
    activeGuests: 'Aktive Gäste',
    pendingApprovals: 'Ausstehende Freigaben',
    sendNotification: 'Benachrichtigung senden',
    notificationTitle: 'Titel',
    notificationMessage: 'Nachricht',
    audience: 'Zielgruppe',
    audienceAll: 'Personal und Gäste',
    audienceGuests: 'Nur Gäste',
    audienceStaff: 'Nur Personal',

    calendarPlanner: 'Kalender-Planer',
    addActivity: 'Aktivität hinzufügen',
    selectDate: 'Datum wählen',
    selectTime: 'Zeit wählen',
    activityName: 'Name der Aktivität',
    saveOnly: 'Nur speichern',
    saveAndNotify: 'Speichern & Benachrichtigen',
    programSaved: 'Programm gespeichert!',
    notifyingGuests: 'Benachrichtige Gäste…',

    roomRanges: 'Zimmerbereiche',
    roomRangesDesc: 'Gültige Zimmernummer-Bereiche für die Gast-Registrierung konfigurieren',
    addRange: 'Bereich hinzufügen',
    saveRanges: 'Bereiche speichern',
    start: 'Start',
    end: 'Ende',

    errorGeneric: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
    errorNetwork: 'Netzwerkfehler. Bitte Verbindung prüfen.',
    errorPermission: 'Sie haben dafür keine Berechtigung.',
    sessionExpired: 'Sitzung abgelaufen — bitte erneut anmelden.',
    copied: 'In die Zwischenablage kopiert',

    hotelServices: 'Hotelservices',
    view: 'Ansehen',
    liveActivitiesTitle: 'Live-Aktivitäten',
    noLiveActivitiesNow: 'Aktuell keine Live-Aktivitäten.',
    communityMode: 'Community-Modus',
    appInformation: 'App-Informationen',
    version: 'Version',
    activityFull: 'Aktivität voll',
    joinNow: 'Jetzt teilnehmen',
    notesLabel: 'Notizen',
    noActivitiesScheduled: 'Keine Aktivitäten geplant',
    pickLuckyTitle: 'Wähle deine Glückszahlen (1-100)',
    dailyLuckyNumber: 'Glückszahl des Tages',
    luckyRule1: 'Jede Person wählt ihre eigene Zahl von 1 bis 100',
    luckyRule2: 'Einträge für Kinder und Familie hinzufügen',
    luckyRule3: 'Jedes Zimmer kann bis zu 4 Einträge pro Tag einreichen',
    luckyRule4: 'Die Verwaltung zieht die Gewinnzahl am Tagesende',
    luckyRule5: 'Die Zahl, die der gezogenen am nächsten ist, gewinnt!',
    howWasActivity: 'Wie war die Aktivität?',
    participantDetails: 'Teilnehmerdaten',
    nameField: 'Name',
    tapPhotoChange: 'Zum Ändern auf das Foto tippen',
    closedToday: 'Heute geschlossen',
    noServicesScheduled: 'Keine Services geplant',
    menu: 'Menü',
    markAllRead: 'Alle als gelesen markieren',
    tapToRead: 'ZUM LESEN TIPPEN',
    noNotificationsYet: 'Noch keine Benachrichtigungen',
    willNotifyYou: 'Wir benachrichtigen dich, sobald etwas passiert',
    thisWeekProgram: 'Programm der Woche',
    liveBadge: 'LIVE',
    justNow: 'Gerade eben',
    musicStoreDescription: 'Entdecke unsere originalen Resort-Songs, Kids-Disco-Musik, Pool-Party-Tracks und Urlaubs-Vibes.',
    exploreMusicStore: 'Music Store entdecken',
    luckyLockedTitle: 'Deine Glückszahlen sind gesperrt',
    luckyLockedMessage: '🎲 Die Ziehung ist bereit! Bleib dran — das Animationsteam startet die Ziehung in Kürze.',
    luckyTimeRemaining: 'Verbleibende Zeit, um deine Glückszahlen zu wählen',
    luckyPickHint: 'Wähle bis zu 4 Glückszahlen (1–100) für dein Zimmer',
    editNumbers: 'Zahlen bearbeiten',
    pickNumbers: 'Zahlen wählen',
    welcomeGreeting: 'Willkommen',
    musicStoreTitle: 'Music Store',
    musicStoreSubtitle: 'Original-Tracks unseres Animationsteams. Für immer deins.',
    exitMusicStore: 'Music Store verlassen',
    loadingMusic: 'Musik wird geladen…',
    storeOpeningSoon: 'Store öffnet bald',
    storeNewTracksMsg: 'Neue Tracks erscheinen hier, sobald das Team sie veröffentlicht.',
    myPurchases: 'Meine Einkäufe',
    myPurchasesSubtitle: 'Lade jeden gekauften Titel jederzeit erneut herunter — ohne Aufpreis.',
    loadingPurchases: 'Deine Einkäufe werden geladen…',
    noPurchases: 'Du hast noch keine Titel gekauft.',
    purchased: 'Gekauft',
    buyBtn: 'Kaufen',
    downloadBtn: 'Herunterladen',
    orderPrefix: 'Bestellung',
    buyPrefix: 'Kaufen',
    buyReviewMsg: 'Überprüfe deinen Kauf und akzeptiere die Bedingungen zur privaten Nutzung, um fortzufahren.',
    songLabel: 'Titel',
    priceLabel: 'Preis',
    buyerLabel: 'Käufer',
    secureCheckoutBy: 'Sichere Zahlung über',
    secureCheckoutTail: 'Du wirst weitergeleitet, um deine Zahlungsdaten einzugeben.',
    payLabel: 'Zahlen',
    couldNotLoadStore: 'Music Store konnte nicht geladen werden. Bitte erneut versuchen.',
    previewUnavailable: 'Vorschau derzeit nicht verfügbar.',
    pleaseAcceptAgreement: 'Bitte akzeptiere die Nutzungsvereinbarung, um fortzufahren.',
    couldNotStartPayment: 'Zahlung konnte nicht gestartet werden.',
    personalUseAgreementText: 'Ich stimme zu, dass dieser Titel ausschließlich für den persönlichen Gebrauch gekauft wird. Ich werde ihn nicht hochladen, weiterverkaufen, weiterverteilen, öffentlich teilen oder ohne schriftliche Erlaubnis von IC FunFlow kommerziell nutzen.',
    luckyInstructions: 'Jede Person wählt ihre eigene Glückszahl. Trage auch Einträge für Familienmitglieder ein!',
    deadlineLabel: 'Frist',
    deadlineClosed: '(GESCHLOSSEN)',
    yourRoomsEntriesToday: 'Einträge deines Zimmers heute:',
    roomLabel: 'Zimmer',
    saveBtn: 'Speichern',
    editBtn: 'Bearbeiten',
    submissionsClosed: 'Einreichungen für heute geschlossen!',
    submissionsClosedMsg: 'Die Frist war {time}. Viel Glück mit deinen bestehenden Einträgen!',
    allLuckySubmitted: 'Alle Glückszahlen sind eingereicht!',
    goodLuckWinners: 'Viel Glück! Die Gewinner werden am Tagesende bekannt gegeben.',
    editUntilMsg: 'Du kannst deine Zahlen oben noch bis {time} bearbeiten.',
    roomEntriesToday: 'Zimmer-Einträge heute',
    entriesRemaining: 'Eintrag(e) für dein Zimmer verbleibend',
    roomLimitReached: 'Dein Zimmer hat das Tageslimit von 4 Einträgen erreicht',
    newEntryPrefix: 'Neuer Eintrag',
    luckyNumberLabel: 'Glückszahl (1-100)',
    addAnotherParticipant: 'Weiteren Teilnehmer hinzufügen',
    submitLuckyOne: 'Glückszahl absenden',
    submitLuckyMany: '{n} Glückszahlen absenden',
    todaysLuckyNumber: 'Glückszahl des Tages',
    winningNumber: 'Gewinnzahl',
    congratulationsPrefix: 'Glückwunsch',
    installTitle: 'Installiere IC FunFlow für die beste Erfahrung',
    installBtn: 'Installieren',
    installLater: 'Später',
    iosInstallHint: 'Tippe auf Teilen → „Zum Home-Bildschirm"',
    howItWorks: 'So funktioniert es:',
    luckyRuleEditUntil: 'Du kannst deine Zahlen bis {time} bearbeiten',
    toastPushEnabled: 'Push-Benachrichtigungen aktiviert!',
    toastPushBlocked: 'Benachrichtigungen blockiert. Bitte in den Browser-Einstellungen aktivieren.',
    toastPushDisabled: 'Push-Benachrichtigungen deaktiviert',
    toastSavePrefFailed: 'Einstellung konnte nicht gespeichert werden',
    toastSignupCancelled: 'Anmeldung storniert',
    toastPleaseRate: 'Bitte eine Bewertung auswählen',
    toastFeedbackThanks: 'Danke für dein Feedback!',
    toastFeedbackFailed: 'Feedback konnte nicht gesendet werden',
    toastInvalidLucky: 'Bitte eine gültige Zahl eingeben (1-100)',
    toastLuckyUpdated: 'Glückszahl aktualisiert!',
    toastLuckyCancelled: 'Eintrag der Glückszahl storniert',
    addToHomeScreenHint: 'Zum Startbildschirm hinzufügen für die beste Erfahrung',
    awayModeMessage: 'Um einen neuen Aufenthalt zu beginnen, wende dich bitte an unser Animationsteam.',
    todayBadge: 'Heute',
    commentsPlaceholder: 'Kommentare? (optional)',
    menuViewer: 'Menü-Ansicht',
    notificationDetails: 'Benachrichtigungsdetails',
    catAdults: 'Erwachsene',
    catKids: 'Kinder',
    catAllAges: 'Alle Altersstufen',
    catEveningAdults: 'Abend Erwachsene',
    catEveningKids: 'Abend Kinder',
    catKidsClub: 'Kids Club',
    catSports: 'Sport',
    catDj: 'DJ',
    // Welcome page (added Feb 2026 — first-visit language selector)
    resortEntertainment: 'Resort-Animation',
    welcomeHeroLine1: 'Ihr Aufenthalt,',
    welcomeHeroLine2: 'verstärkt.',
    welcomeHeroSubtitle: 'Melden Sie sich bei Ihrem Zimmerkonto an, erstellen Sie eines für Ihren Aufenthalt oder schauen Sie sich einfach um — kein Konto nötig.',
    welcomeChipDaily: 'Tagesprogramm',
    welcomeChipEvents: 'Besondere Veranstaltungen',
    welcomeChipMusic: 'Music Store',
    welcomeSigninDesc: 'Wiederkehrender Gast oder Teammitglied.',
    welcomeCreateDesc: 'Zum ersten Mal? Richten Sie Ihr Aufenthaltskonto ein.',
    welcomeExploreTitle: 'Als Besucher erkunden',
    welcomeExploreDesc: 'Keine Anmeldung nötig. Durchstöbern Sie Programm, Events & Store.',
    welcomeFooterHint: 'Buchungen, Feedback und Glückszahl erfordern ein Konto. Musikkäufe funktionieren auch als Besucher.',
    // Auth page (added Feb 2026 — carry language across login/register)
    signingIn: 'Anmeldung läuft…',
    creatingAccount: 'Konto wird erstellt…',
    joinUs: 'Mach mit!',
    createAccountSubtitle: 'Erstellen Sie Ihr Konto, um zu starten',
    iAmA: 'Ich bin ein:',
    guest: 'Gast',
    animatorRole: 'Animator',
    // Explore as Visitor (added Feb 2026 — carry Welcome language into /explore)
    exploreBrand: 'IC FunFlow — Erkunden',
    exploreBackWelcome: 'Startseite',
    exploreJoin: 'Beitreten',
    exploreTabProgramme: 'Programm',
    exploreTabFoodBeverage: 'Gastronomie',
    exploreTabEvents: 'Besondere Veranstaltungen',
    exploreTabStore: 'Music Store',
    exploreTabNews: 'Neuigkeiten',
    exploreTodaysProgramme: 'Heutiges Programm',
    exploreLoading: 'Lädt…',
    exploreSelectTitle: 'Welches Hotel möchten Sie erkunden?',
    exploreSelectSubtitle: 'Hotel wählen',
    exploreSelectDesc: 'Programm, Gastronomie, Events und Musik werden pro Haus kuratiert. Wählen Sie eines aus.',
    exploreSelectLoading: 'Hotels werden geladen…',
    exploreSelectLoadError: 'Hotels konnten nicht geladen werden.',
    exploreSelectEmpty: 'Momentan sind keine aktiven Hotels verfügbar.',
    exploreSelectContinue: 'Weiter',
    exploreSelectStaleHotel: 'Dieses Hotel ist nicht mehr verfügbar — bitte wählen Sie ein anderes.',
    exploreChangeHotel: 'Ändern',
    exploreDisabledTitle: 'Explore ist in diesem Hotel nicht verfügbar',
    exploreDisabledDesc: 'Bitte wenden Sie sich an die Rezeption für Programm und Services.',
    exploreNoSectionsTitle: 'Noch keine Bereiche',
    exploreNoSectionsDesc: 'Dieses Hotel hat noch keine Besucher-Inhalte veröffentlicht.',
    exploreLoadingStore: 'Store wird geladen…',
    exploreLoadingEvents: 'Veranstaltungen werden geladen…',
    exploreLoadingFB: 'Gastronomie wird geladen…',
    exploreLoadingUpdates: 'Neuigkeiten werden geladen…',
    exploreNothingOnTodayTitle: 'Heute nichts geplant',
    exploreNothingOnTodayDesc: 'Das heutige Programm wurde noch nicht veröffentlicht. Bitte schauen Sie später erneut vorbei oder besuchen Sie unser Animationsteam.',
    exploreUnlockWeeklyTitle: 'Erstellen Sie ein Konto, um das gesamte Wochenprogramm freizuschalten.',
    exploreUnlockWeeklyDesc: 'Sehen Sie alle Aktivitäten der nächsten 14 Tage und buchen Sie direkt vom Dashboard.',
    exploreStoreEmptyTitle: 'Store öffnet bald',
    exploreStoreEmptyDesc: 'Es sind noch keine Tracks verfügbar. Bitte später erneut vorbeischauen.',
    exploreEventsEmptyTitle: 'Keine besonderen Veranstaltungen geplant',
    exploreEventsEmptyDesc: 'Live-Shows und Themenabende erscheinen hier, sobald das Team sie veröffentlicht.',
    exploreEventBadge: 'Besondere Veranstaltung',
    exploreFBComingSoonTitle: 'Demnächst',
    exploreFBComingSoonDesc: 'Informationen zur Gastronomie erscheinen hier, sobald das Hotel sie veröffentlicht.',
    exploreFBNothingPublished: 'Noch nichts veröffentlicht — bald wieder vorbeischauen.',
    exploreFBNoInfo: 'Noch keine Informationen veröffentlicht.',
    exploreNewsEmptyTitle: 'Aktuell nichts Neues',
    exploreNewsEmptyDesc: 'Sobald das Team eine Ankündigung sendet, erscheint sie hier.',
    exploreNewsUpdateBadge: 'Update',
    // Food & Beverage card labels (shared DepartmentItemCard — Feb 2026)
    fbClosed: 'Geschlossen',
    fbClosedNow: 'Jetzt geschlossen',
    fbOpenNow: 'Jetzt geöffnet',
    fbOpensAt: 'Öffnet um',
    fbViewWeeklySchedule: 'Wochenplan anzeigen',
    fbHideWeeklySchedule: 'Wochenplan ausblenden',
    fbViewMenu: 'Menü ansehen',
    fbMenus: 'Menüs',
    fbMenu: 'Menü',
    fbWebsite: 'Webseite',
    fbMenuLink: 'Menü-Link',
    fbDressCode: 'Kleiderordnung',
    fbService: 'Service',
  },

  // ─────────────────────────────────────────────────────────────────────
  // ITALIANO
  // ─────────────────────────────────────────────────────────────────────
  it: {
    home: 'Home',
    today: 'Oggi',
    weekly: 'Programma Settimanale',
    week: 'Settimana',
    profile: 'Profilo',
    bookings: 'Prenotazioni',
    logout: 'Esci',
    save: 'Salva',
    saveChanges: 'Salva modifiche',
    cancel: 'Annulla',
    close: 'Chiudi',
    delete: 'Elimina',
    edit: 'Modifica',
    add: 'Aggiungi',
    remove: 'Rimuovi',
    loading: 'Caricamento…',
    search: 'Cerca',
    searchPlaceholder: 'Cerca per nome, camera…',
    noResults: 'Nessun risultato',
    back: 'Indietro',
    done: 'Fatto',
    next: 'Avanti',
    previous: 'Precedente',
    submit: 'Invia',
    confirm: 'Conferma',
    yes: 'Sì',
    no: 'No',
    refresh: 'Aggiorna',
    retry: 'Riprova',
    saving: 'Salvataggio…',
    saved: 'Salvato',
    settings: 'Impostazioni',
    language: 'Lingua',

    welcomeTo: 'Benvenuto su',
    signInSubtitle: 'Accedi per scoprire il programma di oggi',
    login: 'Accedi',
    register: 'Registrati',
    email: 'E-mail',
    password: 'Password',
    forgotPassword: 'Password dimenticata?',
    signIn: 'Accedi',
    createAccount: 'Crea Account',
    fullName: 'Nome completo',
    mainGuestName: 'Ospite principale',
    roomNumber: 'Numero camera',
    occupants: 'Occupanti',
    occupant: 'Occupante',
    addOccupant: 'Aggiungi occupante',
    category: 'Categoria',
    adult: 'Adulto',
    kid: 'Bambino',
    checkInDate: 'Data di arrivo',
    checkOutDate: 'Data di partenza',
    invalidCredentials: 'E-mail o password non validi',
    accountCreated: 'Account creato!',

    welcomeBack: 'Bentornato',
    happeningNow: 'IN CORSO ORA!',
    todayActivities: 'Attività di Oggi',
    adultsActivities: 'Attività per Adulti Oggi',
    kidsActivities: 'Attività per Bambini Oggi',
    eveningActivities: 'Attività Serali',
    specialEvents: 'Eventi Speciali',
    luckyDraw: 'Estrazione',
    noAdultActivities: 'Nessuna attività per adulti in programma oggi',
    noKidsActivities: 'Nessuna attività per bambini in programma oggi',
    noActivities: 'Nessuna attività in programma',
    noEveningShows: 'Nessuno spettacolo serale in programma',
    upcomingEvents: 'Prossimi Eventi Speciali',
    myBookings: 'Le mie prenotazioni',
    noBookings: 'Nessuna prenotazione',
    resortNotes: "Note dell'hotel",

    activities: 'Attività',
    allActivities: 'Tutte le attività',
    activity: 'Attività',
    location: 'Luogo',
    time: 'Ora',
    startTime: 'Inizio',
    endTime: 'Fine',
    animator: 'Animatore',
    animators: 'Animatori',
    description: 'Descrizione',
    capacity: 'Capienza',
    spotsLeft: 'posti disponibili',
    full: 'Completo',
    bookNow: 'Prenota',
    booked: 'Prenotato',
    signedUp: 'Iscritto',
    cancelBooking: 'Annulla prenotazione',
    bookActivity: "Prenota l'attività",
    bookingConfirmed: 'Prenotazione confermata!',
    bookingCancelled: 'Prenotazione annullata',
    liveNow: 'IN DIRETTA',
    starting: 'In partenza',
    startsIn: 'Inizia tra',
    minutes: 'minuti',
    showMore: 'Mostra di più',
    showLess: 'Mostra meno',

    liveScoreboard: 'Tabellone in Diretta',
    participants: 'partecipanti',
    winner: 'Vincitore',
    score: 'Punteggio',
    team: 'Squadra',
    round: 'Manche',
    total: 'Totale',
    rank: 'Posizione',
    tournament: 'Torneo',
    results: 'Risultati',
    finalRanking: 'Classifica Finale',
    points: 'Punti',

    feedback: 'Feedback',
    submitFeedback: 'Invia feedback',
    leaveFeedback: 'Lascia un feedback',
    rateActivity: 'Valuta questa attività',
    excellent: 'Eccellente',
    good: 'Buono',
    bad: 'Pessimo',
    comment: 'Commento',
    feedbackThanks: 'Grazie per il tuo feedback!',

    notifications: 'Notifiche',
    noNotifications: 'Nessuna notifica',
    notificationSettings: 'Impostazioni Notifiche',
    notificationPreferences: 'Preferenze Notifiche',
    enablePushNotifications: 'Attiva notifiche push',
    enablePushDesc: 'Se disattivato, non riceverai nessuna notifica push. Puoi riattivarle quando vuoi.',
    browserPermission: 'Permesso del browser',
    browserPermissionBlocked: 'Bloccato nelle impostazioni del browser',
    browserPermissionDesc: 'Consenti a questo browser di ricevere notifiche push',
    activityReminders: 'Promemoria attività',
    activityRemindersDesc: "Ricevi un avviso poco prima dell'inizio di un'attività",
    liveActivityNotifications: 'Notifiche attività in diretta',
    liveActivityDesc: "Ricevi una notifica appena un'attività inizia",
    generalAnnouncements: 'Annunci generali',
    generalAnnouncementsDesc: "Annunci importanti dal team dell'hotel",
    activityAssignments: 'Assegnazioni attività',
    activityAssignmentsDesc: 'Quando ti viene assegnata una nuova attività',
    scheduleChanges: 'Modifiche al programma',
    scheduleChangesDesc: 'Modifiche di orario, luogo o staff per le tue attività',
    couldNotSavePref: 'Impossibile salvare la preferenza. Riprova.',
    loadingPrefs: 'Caricamento preferenze…',
    backToDashboard: 'Torna alla dashboard',

    adminDashboard: 'Dashboard Admin',
    animatorDashboard: 'Dashboard Animatore',
    chiefAnimator: 'Capo Animatore',
    liveMonitor: 'Monitor in Diretta',
    addAnimator: 'Aggiungi animatore',
    evaluate: 'Valuta',
    newSession: 'Nuova Sessione',
    draw: 'Estrai',
    scoreSheetsLibrary: 'Libreria dei punteggi',
    resultsArchive: 'Archivio Risultati',
    categoryImages: 'Immagini delle categorie',
    devMaintenance: 'Sviluppatore / Manutenzione',
    dedupePushTokens: 'Deduplica token push',
    migrateImages: 'Migra immagini',
    enablePush: 'Attiva Push',
    testPush: 'Test Push',
    performanceMode: 'Modalità Prestazioni',
    performanceModeOn: 'Modalità Prestazioni ATTIVA',
    performanceModeOff: 'Modalità Prestazioni NON ATTIVA',
    showAdvancedTools: 'Mostra strumenti avanzati',
    hideAdvancedTools: 'Nascondi strumenti avanzati',
    devMaintenanceHint: 'Strumenti di manutenzione — usali solo per risolvere problemi.',
    totalActivities: 'attività totali',
    activeGuests: 'Ospiti attivi',
    pendingApprovals: 'Approvazioni in sospeso',
    sendNotification: 'Invia notifica',
    notificationTitle: 'Titolo',
    notificationMessage: 'Messaggio',
    audience: 'Destinatari',
    audienceAll: 'Staff e Ospiti',
    audienceGuests: 'Solo ospiti',
    audienceStaff: 'Solo staff',

    calendarPlanner: 'Pianificatore',
    addActivity: 'Aggiungi attività',
    selectDate: 'Seleziona data',
    selectTime: "Seleziona l'ora",
    activityName: "Nome dell'attività",
    saveOnly: 'Salva solo',
    saveAndNotify: 'Salva e Notifica',
    programSaved: 'Programma salvato!',
    notifyingGuests: 'Notifico gli ospiti…',

    roomRanges: 'Intervalli camere',
    roomRangesDesc: 'Configura gli intervalli validi per la registrazione degli ospiti',
    addRange: 'Aggiungi intervallo',
    saveRanges: 'Salva intervalli',
    start: 'Inizio',
    end: 'Fine',

    errorGeneric: 'Qualcosa è andato storto. Riprova.',
    errorNetwork: 'Errore di rete. Controlla la connessione.',
    errorPermission: 'Non hai i permessi per farlo.',
    sessionExpired: 'Sessione scaduta — accedi di nuovo.',
    copied: 'Copiato negli appunti',

    hotelServices: "Servizi dell'hotel",
    view: 'Vedi',
    liveActivitiesTitle: 'Attività in diretta',
    noLiveActivitiesNow: 'Nessuna attività in diretta al momento.',
    communityMode: 'Modalità Community',
    appInformation: "Informazioni sull'app",
    version: 'Versione',
    activityFull: 'Attività al completo',
    joinNow: 'Partecipa ora',
    notesLabel: 'Note',
    noActivitiesScheduled: 'Nessuna attività in programma',
    pickLuckyTitle: 'Scegli i tuoi numeri fortunati (1-100)',
    dailyLuckyNumber: 'Numero fortunato del giorno',
    luckyRule1: 'Ogni persona sceglie il proprio numero da 1 a 100',
    luckyRule2: 'Aggiungi voci per bambini e familiari',
    luckyRule3: 'Ogni camera può inviare fino a 4 voci al giorno',
    luckyRule4: "L'amministrazione estrae il numero vincente a fine giornata",
    luckyRule5: 'Vince il numero più vicino a quello estratto!',
    howWasActivity: "Com'è stata l'attività?",
    participantDetails: 'Dettagli del partecipante',
    nameField: 'Nome',
    tapPhotoChange: 'Tocca la foto per cambiarla',
    closedToday: 'Chiuso oggi',
    noServicesScheduled: 'Nessun servizio in programma',
    menu: 'Menu',
    markAllRead: 'Segna tutto come letto',
    tapToRead: 'TOCCA PER LEGGERE',
    noNotificationsYet: 'Ancora nessuna notifica',
    willNotifyYou: 'Ti avviseremo quando succederà qualcosa',
    thisWeekProgram: 'Programma della settimana',
    liveBadge: 'IN DIRETTA',
    justNow: 'Proprio ora',
    musicStoreDescription: 'Scopri le nostre canzoni originali del resort, la musica per Kids Disco, i brani da Pool Party e le vibrazioni delle vacanze.',
    exploreMusicStore: 'Esplora Music Store',
    luckyLockedTitle: 'I tuoi numeri fortunati sono bloccati',
    luckyLockedMessage: "🎲 L'estrazione è pronta! Resta connesso — il team di animazione avvierà l'estrazione a breve.",
    luckyTimeRemaining: 'Tempo rimasto per scegliere i tuoi numeri fortunati',
    luckyPickHint: 'Scegli fino a 4 numeri fortunati (1–100) per la tua camera',
    editNumbers: 'Modifica numeri',
    pickNumbers: 'Scegli i numeri',
    welcomeGreeting: 'Benvenuto',
    musicStoreTitle: 'Music Store',
    musicStoreSubtitle: 'Brani originali del nostro team di animazione. Tuoi per sempre.',
    exitMusicStore: 'Esci da Music Store',
    loadingMusic: 'Caricamento della musica…',
    storeOpeningSoon: 'Negozio in arrivo',
    storeNewTracksMsg: 'I nuovi brani appariranno qui appena il team li pubblicherà.',
    myPurchases: 'I miei acquisti',
    myPurchasesSubtitle: 'Riscarica ogni brano acquistato — nessun costo aggiuntivo.',
    loadingPurchases: 'Caricamento dei tuoi acquisti…',
    noPurchases: 'Non hai ancora acquistato nessun brano.',
    purchased: 'Acquistato',
    buyBtn: 'Acquista',
    downloadBtn: 'Scarica',
    orderPrefix: 'Ordine',
    buyPrefix: 'Acquista',
    buyReviewMsg: "Rivedi il tuo acquisto e accetta i termini d'uso personale per continuare.",
    songLabel: 'Brano',
    priceLabel: 'Prezzo',
    buyerLabel: 'Acquirente',
    secureCheckoutBy: 'Pagamento sicuro tramite',
    secureCheckoutTail: 'Verrai reindirizzato per inserire i tuoi dati di pagamento.',
    payLabel: 'Paga',
    couldNotLoadStore: "Impossibile caricare Music Store. Riprova.",
    previewUnavailable: "Anteprima non disponibile al momento.",
    pleaseAcceptAgreement: "Accetta l'accordo di uso personale per continuare.",
    couldNotStartPayment: "Impossibile avviare il pagamento.",
    personalUseAgreementText: "Accetto che questo brano venga acquistato solo per uso personale. Non lo caricherò, rivenderò, ridistribuirò, condividerò pubblicamente né utilizzerò a fini commerciali senza autorizzazione scritta di IC FunFlow.",
    luckyInstructions: 'Ogni persona sceglie il proprio numero fortunato. Aggiungi voci per i familiari!',
    deadlineLabel: 'Scadenza',
    deadlineClosed: '(CHIUSO)',
    yourRoomsEntriesToday: 'Voci della tua camera oggi:',
    roomLabel: 'Camera',
    saveBtn: 'Salva',
    editBtn: 'Modifica',
    submissionsClosed: 'Invii chiusi per oggi!',
    submissionsClosedMsg: 'La scadenza era {time}. In bocca al lupo con le tue voci esistenti!',
    allLuckySubmitted: 'Tutti i numeri fortunati inviati!',
    goodLuckWinners: 'In bocca al lupo! I vincitori saranno annunciati a fine giornata.',
    editUntilMsg: 'Puoi ancora modificare i tuoi numeri qui sopra fino alle {time}.',
    roomEntriesToday: 'Voci della camera oggi',
    entriesRemaining: 'voce/i rimanente/i per la tua camera',
    roomLimitReached: 'La tua camera ha raggiunto il limite giornaliero di 4 voci',
    newEntryPrefix: 'Nuova voce',
    luckyNumberLabel: 'Numero fortunato (1-100)',
    addAnotherParticipant: 'Aggiungi un altro partecipante',
    submitLuckyOne: 'Invia numero fortunato',
    submitLuckyMany: 'Invia {n} numeri fortunati',
    todaysLuckyNumber: 'Numero fortunato di oggi',
    winningNumber: 'Numero vincente',
    congratulationsPrefix: 'Congratulazioni',
    installTitle: 'Installa IC FunFlow per la migliore esperienza',
    installBtn: 'Installa',
    installLater: 'Più tardi',
    iosInstallHint: 'Tocca Condividi → "Aggiungi a Home"',
    howItWorks: 'Come funziona:',
    luckyRuleEditUntil: 'Puoi modificare i tuoi numeri fino alle {time}',
    toastPushEnabled: 'Notifiche push attivate!',
    toastPushBlocked: 'Notifiche bloccate. Attivale nelle impostazioni del browser.',
    toastPushDisabled: 'Notifiche push disattivate',
    toastSavePrefFailed: 'Impossibile salvare la preferenza',
    toastSignupCancelled: 'Iscrizione annullata',
    toastPleaseRate: 'Seleziona una valutazione',
    toastFeedbackThanks: 'Grazie per il tuo feedback!',
    toastFeedbackFailed: "Impossibile inviare il feedback",
    toastInvalidLucky: 'Inserisci un numero valido (1-100)',
    toastLuckyUpdated: 'Numero fortunato aggiornato!',
    toastLuckyCancelled: 'Voce del numero fortunato annullata',
    addToHomeScreenHint: 'Aggiungi alla schermata Home per la migliore esperienza',
    awayModeMessage: 'Per iniziare una nuova vacanza, rivolgiti al nostro team di animazione.',
    todayBadge: 'Oggi',
    commentsPlaceholder: 'Qualche commento? (facoltativo)',
    menuViewer: 'Visualizzatore del menu',
    notificationDetails: 'Dettagli della notifica',
    catAdults: 'Adulti',
    catKids: 'Bambini',
    catAllAges: 'Tutte le età',
    catEveningAdults: 'Serale adulti',
    catEveningKids: 'Serale bambini',
    catKidsClub: 'Kids Club',
    catSports: 'Sport',
    catDj: 'DJ',
    // Welcome page (added Feb 2026 — first-visit language selector)
    resortEntertainment: 'Animazione hotel',
    welcomeHeroLine1: 'Il tuo soggiorno,',
    welcomeHeroLine2: 'amplificato.',
    welcomeHeroSubtitle: 'Accedi al tuo account camera, creane uno per il soggiorno o dai un\'occhiata — nessun account necessario.',
    welcomeChipDaily: 'Programma giornaliero',
    welcomeChipEvents: 'Eventi speciali',
    welcomeChipMusic: 'Music store',
    welcomeSigninDesc: 'Ospite di ritorno o membro dello staff.',
    welcomeCreateDesc: 'Prima volta? Crea il tuo account per il soggiorno.',
    welcomeExploreTitle: 'Esplora come visitatore',
    welcomeExploreDesc: 'Nessuna registrazione richiesta. Sfoglia programma, eventi e store.',
    welcomeFooterHint: 'Prenotazioni, feedback e numero fortunato richiedono un account. Gli acquisti musicali funzionano anche come visitatore.',
    // Auth page (added Feb 2026 — carry language across login/register)
    signingIn: 'Accesso in corso…',
    creatingAccount: 'Creazione account…',
    joinUs: 'Unisciti a noi!',
    createAccountSubtitle: 'Crea il tuo account per iniziare',
    iAmA: 'Sono:',
    guest: 'Ospite',
    animatorRole: 'Animatore',
    // Explore as Visitor (added Feb 2026 — carry Welcome language into /explore)
    exploreBrand: 'IC FunFlow — Esplora',
    exploreBackWelcome: 'Home',
    exploreJoin: 'Unisciti',
    exploreTabProgramme: 'Programma',
    exploreTabFoodBeverage: 'Ristorazione',
    exploreTabEvents: 'Eventi Speciali',
    exploreTabStore: 'Music Store',
    exploreTabNews: 'Novità',
    exploreTodaysProgramme: 'Programma di Oggi',
    exploreLoading: 'Caricamento…',
    exploreSelectTitle: 'Quale hotel vuoi esplorare?',
    exploreSelectSubtitle: 'Scegli un hotel',
    exploreSelectDesc: 'Programma, ristorazione, eventi e musica sono curati per ciascuna struttura. Scegline una per vedere cosa c\u2019è ora.',
    exploreSelectLoading: 'Caricamento hotel…',
    exploreSelectLoadError: 'Impossibile caricare gli hotel al momento.',
    exploreSelectEmpty: 'Nessun hotel attivo al momento. Riprova più tardi.',
    exploreSelectContinue: 'Continua',
    exploreSelectStaleHotel: 'Questo hotel non è più disponibile — scegline un altro.',
    exploreChangeHotel: 'Cambia',
    exploreDisabledTitle: 'Explore non è disponibile in questo hotel',
    exploreDisabledDesc: 'Contatta la reception per programma e servizi attuali.',
    exploreNoSectionsTitle: 'Nessuna sezione da mostrare',
    exploreNoSectionsDesc: "Questo hotel non ha ancora pubblicato contenuti per i visitatori.",
    exploreLoadingStore: 'Caricamento del negozio…',
    exploreLoadingEvents: 'Caricamento eventi…',
    exploreLoadingFB: 'Caricamento ristorazione…',
    exploreLoadingUpdates: 'Caricamento novità…',
    exploreNothingOnTodayTitle: 'Nulla in programma oggi',
    exploreNothingOnTodayDesc: 'Il programma di oggi non è ancora stato pubblicato. Torna più tardi o rivolgiti al nostro team di animazione.',
    exploreUnlockWeeklyTitle: "Crea un account per sbloccare l'intero programma settimanale.",
    exploreUnlockWeeklyDesc: 'Vedi ogni attività dei prossimi 14 giorni e prenota direttamente dal tuo pannello.',
    exploreStoreEmptyTitle: 'Negozio in apertura',
    exploreStoreEmptyDesc: 'Nessun brano disponibile per ora. Torna più tardi.',
    exploreEventsEmptyTitle: 'Nessun evento speciale in programma',
    exploreEventsEmptyDesc: 'Spettacoli e serate a tema appariranno qui non appena il team li pubblicherà.',
    exploreEventBadge: 'Evento Speciale',
    exploreFBComingSoonTitle: 'Prossimamente',
    exploreFBComingSoonDesc: 'Le informazioni sulla ristorazione appariranno qui man mano che verranno pubblicate.',
    exploreFBNothingPublished: 'Nulla di pubblicato per ora — torna presto.',
    exploreFBNoInfo: 'Nessuna informazione pubblicata al momento.',
    exploreNewsEmptyTitle: 'Nessuna novità al momento',
    exploreNewsEmptyDesc: 'Quando il team invierà un annuncio, apparirà qui.',
    exploreNewsUpdateBadge: 'Aggiornamento',
    // Food & Beverage card labels (shared DepartmentItemCard — Feb 2026)
    fbClosed: 'Chiuso',
    fbClosedNow: 'Chiuso ora',
    fbOpenNow: 'Aperto ora',
    fbOpensAt: 'Apre alle',
    fbViewWeeklySchedule: 'Vedi orario settimanale',
    fbHideWeeklySchedule: "Nascondi l'orario settimanale",
    fbViewMenu: 'Vedi menu',
    fbMenus: 'Menu',
    fbMenu: 'Menu',
    fbWebsite: 'Sito web',
    fbMenuLink: 'Link del menu',
    fbDressCode: 'Dress code',
    fbService: 'Servizio',
  },

  // ─────────────────────────────────────────────────────────────────────
  // POLSKI
  // ─────────────────────────────────────────────────────────────────────
  pl: {
    home: 'Strona główna',
    today: 'Dziś',
    weekly: 'Program tygodniowy',
    week: 'Tydzień',
    profile: 'Profil',
    bookings: 'Rezerwacje',
    logout: 'Wyloguj się',
    save: 'Zapisz',
    saveChanges: 'Zapisz zmiany',
    cancel: 'Anuluj',
    close: 'Zamknij',
    delete: 'Usuń',
    edit: 'Edytuj',
    add: 'Dodaj',
    remove: 'Usuń',
    loading: 'Ładowanie…',
    search: 'Szukaj',
    searchPlaceholder: 'Szukaj po nazwisku, pokoju…',
    noResults: 'Brak wyników',
    back: 'Wstecz',
    done: 'Gotowe',
    next: 'Dalej',
    previous: 'Wstecz',
    submit: 'Wyślij',
    confirm: 'Potwierdź',
    yes: 'Tak',
    no: 'Nie',
    refresh: 'Odśwież',
    retry: 'Spróbuj ponownie',
    saving: 'Zapisywanie…',
    saved: 'Zapisano',
    settings: 'Ustawienia',
    language: 'Język',

    welcomeTo: 'Witamy w',
    signInSubtitle: 'Zaloguj się, aby poznać dzisiejszy program',
    login: 'Logowanie',
    register: 'Rejestracja',
    email: 'E-mail',
    password: 'Hasło',
    forgotPassword: 'Zapomniałeś hasła?',
    signIn: 'Zaloguj się',
    createAccount: 'Utwórz konto',
    fullName: 'Imię i nazwisko',
    mainGuestName: 'Główny gość',
    roomNumber: 'Numer pokoju',
    occupants: 'Goście',
    occupant: 'Gość',
    addOccupant: 'Dodaj gościa',
    category: 'Kategoria',
    adult: 'Dorosły',
    kid: 'Dziecko',
    checkInDate: 'Data przyjazdu',
    checkOutDate: 'Data wyjazdu',
    invalidCredentials: 'Nieprawidłowy e-mail lub hasło',
    accountCreated: 'Konto utworzone!',

    welcomeBack: 'Witaj ponownie',
    happeningNow: 'DZIEJE SIĘ TERAZ!',
    todayActivities: 'Dzisiejsze zajęcia',
    adultsActivities: 'Zajęcia dla dorosłych dzisiaj',
    kidsActivities: 'Zajęcia dla dzieci dzisiaj',
    eveningActivities: 'Zajęcia wieczorne',
    specialEvents: 'Wydarzenia specjalne',
    luckyDraw: 'Losowanie',
    noAdultActivities: 'Brak zajęć dla dorosłych zaplanowanych na dzisiaj',
    noKidsActivities: 'Brak zajęć dla dzieci zaplanowanych na dzisiaj',
    noActivities: 'Brak zaplanowanych zajęć',
    noEveningShows: 'Brak zaplanowanych występów wieczornych',
    upcomingEvents: 'Nadchodzące wydarzenia specjalne',
    myBookings: 'Moje rezerwacje',
    noBookings: 'Brak rezerwacji',
    resortNotes: 'Notatki hotelu',

    activities: 'Zajęcia',
    allActivities: 'Wszystkie zajęcia',
    activity: 'Zajęcia',
    location: 'Miejsce',
    time: 'Godzina',
    startTime: 'Godzina rozpoczęcia',
    endTime: 'Godzina zakończenia',
    animator: 'Animator',
    animators: 'Animatorzy',
    description: 'Opis',
    capacity: 'Miejsca',
    spotsLeft: 'wolnych miejsc',
    full: 'Brak miejsc',
    bookNow: 'Zarezerwuj',
    booked: 'Zarezerwowane',
    signedUp: 'Zapisany',
    cancelBooking: 'Anuluj rezerwację',
    bookActivity: 'Zarezerwuj zajęcia',
    bookingConfirmed: 'Rezerwacja potwierdzona!',
    bookingCancelled: 'Rezerwacja anulowana',
    liveNow: 'NA ŻYWO',
    starting: 'Rozpoczyna się',
    startsIn: 'Rozpocznie się za',
    minutes: 'min',
    showMore: 'Pokaż więcej',
    showLess: 'Pokaż mniej',

    liveScoreboard: 'Wyniki na żywo',
    participants: 'uczestnicy',
    winner: 'Zwycięzca',
    score: 'Wynik',
    team: 'Drużyna',
    round: 'Runda',
    total: 'Suma',
    rank: 'Miejsce',
    tournament: 'Turniej',
    results: 'Wyniki',
    finalRanking: 'Klasyfikacja końcowa',
    points: 'Punkty',

    feedback: 'Opinia',
    submitFeedback: 'Wyślij opinię',
    leaveFeedback: 'Zostaw opinię',
    rateActivity: 'Oceń te zajęcia',
    excellent: 'Świetnie',
    good: 'Dobrze',
    bad: 'Źle',
    comment: 'Komentarz',
    feedbackThanks: 'Dziękujemy za opinię!',

    notifications: 'Powiadomienia',
    noNotifications: 'Brak powiadomień',
    notificationSettings: 'Ustawienia powiadomień',
    notificationPreferences: 'Preferencje powiadomień',
    enablePushNotifications: 'Włącz powiadomienia push',
    enablePushDesc: 'Gdy wyłączone, nie otrzymasz żadnych powiadomień push. Możesz je włączyć ponownie w każdej chwili.',
    browserPermission: 'Uprawnienia przeglądarki',
    browserPermissionBlocked: 'Zablokowane w ustawieniach przeglądarki',
    browserPermissionDesc: 'Zezwól tej przeglądarce na odbiór powiadomień push',
    activityReminders: 'Przypomnienia o zajęciach',
    activityRemindersDesc: 'Otrzymuj powiadomienie tuż przed rozpoczęciem zajęć',
    liveActivityNotifications: 'Powiadomienia o zajęciach na żywo',
    liveActivityDesc: 'Otrzymaj powiadomienie w chwili rozpoczęcia zajęć',
    generalAnnouncements: 'Ogłoszenia ogólne',
    generalAnnouncementsDesc: 'Ważne ogłoszenia z całego hotelu od zespołu',
    activityAssignments: 'Przydział do zajęć',
    activityAssignmentsDesc: 'Gdy zostaniesz przypisany do nowych zajęć',
    scheduleChanges: 'Zmiany w programie',
    scheduleChangesDesc: 'Zmiany czasu, miejsca lub zespołu w Twoich zajęciach',
    couldNotSavePref: 'Nie udało się zapisać preferencji. Spróbuj ponownie.',
    loadingPrefs: 'Ładowanie preferencji…',
    backToDashboard: 'Powrót do panelu',

    adminDashboard: 'Panel administratora',
    animatorDashboard: 'Panel animatora',
    chiefAnimator: 'Główny animator',
    liveMonitor: 'Monitor na żywo',
    addAnimator: 'Dodaj animatora',
    evaluate: 'Oceń',
    newSession: 'Nowa sesja',
    draw: 'Losuj',
    scoreSheetsLibrary: 'Biblioteka arkuszy wyników',
    resultsArchive: 'Archiwum wyników',
    categoryImages: 'Obrazy kategorii',
    devMaintenance: 'Deweloper / Konserwacja',
    dedupePushTokens: 'Deduplikuj tokeny push',
    migrateImages: 'Migruj obrazy',
    enablePush: 'Włącz push',
    testPush: 'Testuj push',
    performanceMode: 'Tryb wydajności',
    performanceModeOn: 'Tryb wydajności WŁ.',
    performanceModeOff: 'Tryb wydajności WYŁ.',
    showAdvancedTools: 'Pokaż zaawansowane narzędzia',
    hideAdvancedTools: 'Ukryj zaawansowane narzędzia',
    devMaintenanceHint: 'Narzędzia konserwacyjne — używaj tylko podczas rozwiązywania problemów.',
    totalActivities: 'zajęć łącznie',
    activeGuests: 'Aktywni goście',
    pendingApprovals: 'Oczekujące zatwierdzenia',
    sendNotification: 'Wyślij powiadomienie',
    notificationTitle: 'Tytuł',
    notificationMessage: 'Wiadomość',
    audience: 'Odbiorcy',
    audienceAll: 'Zespół i goście',
    audienceGuests: 'Tylko goście',
    audienceStaff: 'Tylko zespół',

    calendarPlanner: 'Planer kalendarza',
    addActivity: 'Dodaj zajęcia',
    selectDate: 'Wybierz datę',
    selectTime: 'Wybierz godzinę',
    activityName: 'Nazwa zajęć',
    saveOnly: 'Tylko zapisz',
    saveAndNotify: 'Zapisz i powiadom',
    programSaved: 'Program zapisany!',
    notifyingGuests: 'Powiadamianie gości…',

    roomRanges: 'Zakresy pokoi',
    roomRangesDesc: 'Skonfiguruj poprawne zakresy numerów pokoi dla rejestracji gości',
    addRange: 'Dodaj zakres',
    saveRanges: 'Zapisz zakresy',
    start: 'Początek',
    end: 'Koniec',

    errorGeneric: 'Coś poszło nie tak. Spróbuj ponownie.',
    errorNetwork: 'Błąd sieci. Sprawdź połączenie.',
    errorPermission: 'Nie masz uprawnień do tej czynności.',
    sessionExpired: 'Sesja wygasła — zaloguj się ponownie.',
    copied: 'Skopiowano do schowka',

    hotelServices: 'Usługi hotelowe',
    view: 'Zobacz',
    liveActivitiesTitle: 'Zajęcia na żywo',
    noLiveActivitiesNow: 'Brak zajęć na żywo w tej chwili.',
    communityMode: 'Tryb społeczności',
    appInformation: 'Informacje o aplikacji',
    version: 'Wersja',
    activityFull: 'Brak miejsc',
    joinNow: 'Dołącz teraz',
    notesLabel: 'Notatki',
    noActivitiesScheduled: 'Brak zaplanowanych zajęć',
    pickLuckyTitle: 'Wybierz swoje szczęśliwe numery (1-100)',
    dailyLuckyNumber: 'Szczęśliwy numer dnia',
    luckyRule1: 'Każdy wybiera swój własny numer od 1 do 100',
    luckyRule2: 'Dodaj wpisy dla dzieci i członków rodziny',
    luckyRule3: 'Każdy pokój może zgłosić do 4 wpisów dziennie',
    luckyRule4: 'Administracja losuje zwycięski numer pod koniec dnia',
    luckyRule5: 'Wygrywa numer najbliższy wylosowanemu!',
    howWasActivity: 'Jak Ci się podobały zajęcia?',
    participantDetails: 'Dane uczestnika',
    nameField: 'Imię',
    tapPhotoChange: 'Dotknij zdjęcia, aby je zmienić',
    closedToday: 'Zamknięte dzisiaj',
    noServicesScheduled: 'Brak zaplanowanych usług',
    menu: 'Menu',
    markAllRead: 'Oznacz wszystkie jako przeczytane',
    tapToRead: 'DOTKNIJ, ABY PRZECZYTAĆ',
    noNotificationsYet: 'Jeszcze brak powiadomień',
    willNotifyYou: 'Powiadomimy Cię, gdy coś się wydarzy',
    thisWeekProgram: 'Program tego tygodnia',
    liveBadge: 'NA ŻYWO',
    justNow: 'Właśnie teraz',
    musicStoreDescription: 'Odkryj nasze oryginalne piosenki hotelowe, muzykę Kids Disco, utwory Pool Party i wakacyjny klimat.',
    exploreMusicStore: 'Odkryj Music Store',
    luckyLockedTitle: 'Twoje szczęśliwe numery są zablokowane',
    luckyLockedMessage: '🎲 Losowanie jest gotowe! Bądź w kontakcie — zespół animacji zaraz rozpocznie losowanie.',
    luckyTimeRemaining: 'Pozostały czas na wybór szczęśliwych numerów',
    luckyPickHint: 'Wybierz do 4 szczęśliwych numerów (1–100) dla swojego pokoju',
    editNumbers: 'Edytuj numery',
    pickNumbers: 'Wybierz numery',
    welcomeGreeting: 'Witaj',
    musicStoreTitle: 'Music Store',
    musicStoreSubtitle: 'Oryginalne utwory naszego zespołu animacji. Twoje na zawsze.',
    exitMusicStore: 'Zamknij Music Store',
    loadingMusic: 'Ładowanie muzyki…',
    storeOpeningSoon: 'Sklep otwierany wkrótce',
    storeNewTracksMsg: 'Nowe utwory pojawią się tutaj, gdy tylko zespół je opublikuje.',
    myPurchases: 'Moje zakupy',
    myPurchasesSubtitle: 'Pobierz ponownie każdy zakupiony utwór — bez dodatkowych opłat.',
    loadingPurchases: 'Ładowanie zakupów…',
    noPurchases: 'Nie kupiłeś jeszcze żadnego utworu.',
    purchased: 'Zakupione',
    buyBtn: 'Kup',
    downloadBtn: 'Pobierz',
    orderPrefix: 'Zamówienie',
    buyPrefix: 'Kup',
    buyReviewMsg: 'Sprawdź swój zakup i zaakceptuj warunki użytku osobistego, aby kontynuować.',
    songLabel: 'Utwór',
    priceLabel: 'Cena',
    buyerLabel: 'Kupujący',
    secureCheckoutBy: 'Bezpieczna płatność przez',
    secureCheckoutTail: 'Zostaniesz przekierowany, aby wprowadzić dane płatności.',
    payLabel: 'Zapłać',
    couldNotLoadStore: 'Nie udało się załadować Music Store. Spróbuj ponownie.',
    previewUnavailable: 'Podgląd jest obecnie niedostępny.',
    pleaseAcceptAgreement: 'Zaakceptuj umowę o użytku osobistym, aby kontynuować.',
    couldNotStartPayment: 'Nie udało się rozpocząć płatności.',
    personalUseAgreementText: 'Zgadzam się, że ten utwór jest kupowany wyłącznie do użytku osobistego. Nie będę go przesyłać, odsprzedawać, redystrybuować, udostępniać publicznie ani wykorzystywać komercyjnie bez pisemnej zgody IC FunFlow.',
    luckyInstructions: 'Każdy wybiera swój szczęśliwy numer. Dodaj wpisy dla członków rodziny!',
    deadlineLabel: 'Termin',
    deadlineClosed: '(ZAMKNIĘTE)',
    yourRoomsEntriesToday: 'Dzisiejsze wpisy Twojego pokoju:',
    roomLabel: 'Pokój',
    saveBtn: 'Zapisz',
    editBtn: 'Edytuj',
    submissionsClosed: 'Zgłoszenia na dziś zamknięte!',
    submissionsClosedMsg: 'Termin minął o {time}. Powodzenia z istniejącymi wpisami!',
    allLuckySubmitted: 'Wszystkie szczęśliwe numery zgłoszone!',
    goodLuckWinners: 'Powodzenia! Zwycięzcy zostaną ogłoszeni pod koniec dnia.',
    editUntilMsg: 'Możesz edytować swoje numery powyżej do {time}.',
    roomEntriesToday: 'Dzisiejsze wpisy pokoju',
    entriesRemaining: 'wpis(y) pozostały dla Twojego pokoju',
    roomLimitReached: 'Twój pokój osiągnął dzienny limit 4 wpisów',
    newEntryPrefix: 'Nowy wpis',
    luckyNumberLabel: 'Szczęśliwy numer (1-100)',
    addAnotherParticipant: 'Dodaj kolejnego uczestnika',
    submitLuckyOne: 'Wyślij szczęśliwy numer',
    submitLuckyMany: 'Wyślij {n} szczęśliwych numerów',
    todaysLuckyNumber: 'Dzisiejszy szczęśliwy numer',
    winningNumber: 'Zwycięski numer',
    congratulationsPrefix: 'Gratulacje',
    installTitle: 'Zainstaluj IC FunFlow, aby uzyskać najlepsze wrażenia',
    installBtn: 'Zainstaluj',
    installLater: 'Później',
    iosInstallHint: 'Dotknij Udostępnij → „Do ekranu głównego"',
    howItWorks: 'Jak to działa:',
    luckyRuleEditUntil: 'Możesz edytować swoje numery do {time}',
    toastPushEnabled: 'Powiadomienia push włączone!',
    toastPushBlocked: 'Powiadomienia zablokowane. Włącz je w ustawieniach przeglądarki.',
    toastPushDisabled: 'Powiadomienia push wyłączone',
    toastSavePrefFailed: 'Nie udało się zapisać preferencji',
    toastSignupCancelled: 'Rejestracja anulowana',
    toastPleaseRate: 'Wybierz ocenę',
    toastFeedbackThanks: 'Dziękujemy za Twoją opinię!',
    toastFeedbackFailed: 'Nie udało się wysłać opinii',
    toastInvalidLucky: 'Wprowadź poprawny numer (1-100)',
    toastLuckyUpdated: 'Szczęśliwy numer zaktualizowany!',
    toastLuckyCancelled: 'Wpis szczęśliwego numeru anulowany',
    addToHomeScreenHint: 'Dodaj do ekranu głównego, aby uzyskać najlepsze wrażenia',
    awayModeMessage: 'Aby rozpocząć nowy pobyt, odwiedź nasz zespół animacji.',
    todayBadge: 'Dziś',
    commentsPlaceholder: 'Jakieś komentarze? (opcjonalnie)',
    menuViewer: 'Podgląd menu',
    notificationDetails: 'Szczegóły powiadomienia',
    catAdults: 'Dorośli',
    catKids: 'Dzieci',
    catAllAges: 'Każdy wiek',
    catEveningAdults: 'Wieczór dorośli',
    catEveningKids: 'Wieczór dzieci',
    catKidsClub: 'Kids Club',
    catSports: 'Sport',
    catDj: 'DJ',
    // Welcome page (added Feb 2026 — first-visit language selector)
    resortEntertainment: 'Animacja hotelowa',
    welcomeHeroLine1: 'Twój pobyt,',
    welcomeHeroLine2: 'wzmocniony.',
    welcomeHeroSubtitle: 'Zaloguj się do konta pokoju, utwórz je na czas pobytu lub po prostu się rozejrzyj — konto nie jest wymagane.',
    welcomeChipDaily: 'Program dnia',
    welcomeChipEvents: 'Wydarzenia specjalne',
    welcomeChipMusic: 'Music store',
    welcomeSigninDesc: 'Powracający gość lub członek zespołu.',
    welcomeCreateDesc: 'Pierwszy raz? Załóż konto na czas pobytu.',
    welcomeExploreTitle: 'Zwiedzaj jako gość',
    welcomeExploreDesc: 'Bez rejestracji. Przeglądaj program, wydarzenia i sklep.',
    welcomeFooterHint: 'Rezerwacje, opinie i szczęśliwy numer wymagają konta pobytu. Zakupy muzyki działają również dla gości.',
    // Auth page (added Feb 2026 — carry language across login/register)
    signingIn: 'Logowanie…',
    creatingAccount: 'Tworzenie konta…',
    joinUs: 'Dołącz do nas!',
    createAccountSubtitle: 'Utwórz konto, aby zacząć',
    iAmA: 'Jestem:',
    guest: 'Gość',
    animatorRole: 'Animator',
    // Explore as Visitor (added Feb 2026 — carry Welcome language into /explore)
    exploreBrand: 'IC FunFlow — Odkrywaj',
    exploreBackWelcome: 'Start',
    exploreJoin: 'Dołącz',
    exploreTabProgramme: 'Program',
    exploreTabFoodBeverage: 'Gastronomia',
    exploreTabEvents: 'Wydarzenia Specjalne',
    exploreTabStore: 'Music Store',
    exploreTabNews: 'Aktualności',
    exploreTodaysProgramme: 'Program na Dziś',
    exploreLoading: 'Ładowanie…',
    exploreSelectTitle: 'Który hotel chcesz odkryć?',
    exploreSelectSubtitle: 'Wybierz hotel',
    exploreSelectDesc: 'Program, gastronomia, wydarzenia i muzyka są dobierane osobno dla każdego obiektu. Wybierz jeden, aby zobaczyć aktualną ofertę.',
    exploreSelectLoading: 'Ładowanie hoteli…',
    exploreSelectLoadError: 'Nie można teraz załadować hoteli.',
    exploreSelectEmpty: 'Brak aktywnych hoteli. Sprawdź ponownie później.',
    exploreSelectContinue: 'Dalej',
    exploreSelectStaleHotel: 'Ten hotel nie jest już dostępny — wybierz inny.',
    exploreChangeHotel: 'Zmień',
    exploreDisabledTitle: 'Sekcja Explore nie jest dostępna w tym hotelu',
    exploreDisabledDesc: 'Skontaktuj się z recepcją w sprawie aktualnego programu i usług.',
    exploreNoSectionsTitle: 'Brak sekcji do wyświetlenia',
    exploreNoSectionsDesc: 'Ten hotel nie opublikował jeszcze treści dla odwiedzających.',
    exploreLoadingStore: 'Ładowanie sklepu…',
    exploreLoadingEvents: 'Ładowanie wydarzeń…',
    exploreLoadingFB: 'Ładowanie gastronomii…',
    exploreLoadingUpdates: 'Ładowanie aktualności…',
    exploreNothingOnTodayTitle: 'Nic zaplanowanego na dziś',
    exploreNothingOnTodayDesc: 'Dzisiejszy program nie został jeszcze opublikowany. Sprawdź później lub odwiedź nasz zespół animacji.',
    exploreUnlockWeeklyTitle: 'Załóż konto, aby odblokować cały tygodniowy program.',
    exploreUnlockWeeklyDesc: 'Zobacz każdą aktywność na najbliższe 14 dni i rezerwuj bezpośrednio z panelu.',
    exploreStoreEmptyTitle: 'Sklep wkrótce',
    exploreStoreEmptyDesc: 'Nie ma jeszcze dostępnych utworów. Sprawdź później.',
    exploreEventsEmptyTitle: 'Brak zaplanowanych wydarzeń specjalnych',
    exploreEventsEmptyDesc: 'Występy i wieczory tematyczne pojawią się tutaj, gdy tylko zespół je opublikuje.',
    exploreEventBadge: 'Wydarzenie Specjalne',
    exploreFBComingSoonTitle: 'Wkrótce',
    exploreFBComingSoonDesc: 'Informacje o gastronomii pojawią się tutaj po publikacji przez hotel.',
    exploreFBNothingPublished: 'Nic jeszcze nie opublikowano — sprawdź wkrótce.',
    exploreFBNoInfo: 'Brak opublikowanych informacji.',
    exploreNewsEmptyTitle: 'Nic nowego',
    exploreNewsEmptyDesc: 'Kiedy zespół wyśle ogłoszenie, pojawi się tutaj.',
    exploreNewsUpdateBadge: 'Aktualizacja',
    // Food & Beverage card labels (shared DepartmentItemCard — Feb 2026)
    fbClosed: 'Zamknięte',
    fbClosedNow: 'Zamknięte',
    fbOpenNow: 'Otwarte teraz',
    fbOpensAt: 'Otwiera się o',
    fbViewWeeklySchedule: 'Zobacz plan tygodnia',
    fbHideWeeklySchedule: 'Ukryj plan tygodnia',
    fbViewMenu: 'Zobacz menu',
    fbMenus: 'Menu',
    fbMenu: 'Menu',
    fbWebsite: 'Strona www',
    fbMenuLink: 'Link do menu',
    fbDressCode: 'Ubiór',
    fbService: 'Usługa',
  },
};

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
];

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('app_language') || 'en');

  const setLanguage = useCallback((code) => {
    setLang(code);
    localStorage.setItem('app_language', code);
    // Best-effort sync to the server so backend-generated push
    // notifications translate into the guest's chosen language before
    // dispatch. Silently ignored when the guest isn't signed in OR when
    // we're on a public visitor path (Welcome / Explore / Auth / Order) —
    // otherwise a stale token would 401 here and (via the global axios
    // interceptor) surface a spurious "session expired" toast.
    try {
      const token = localStorage.getItem('token');
      const pathname = (typeof window !== 'undefined' && window.location)
        ? window.location.pathname
        : '';
      const onPublicPath = pathname === '/'
        || pathname.startsWith('/explore')
        || pathname.startsWith('/auth')
        || pathname.startsWith('/order');
      if (token && !onPublicPath && API_URL) {
        axios.put(
          `${API_URL}/api/translate/me/language`,
          { language: code },
          { headers: { Authorization: `Bearer ${token}` } },
        ).catch(() => {});
      }
    } catch (_) { /* ignore */ }
  }, []);

  const t = useCallback((key) => {
    return translations[lang]?.[key] || translations.en?.[key] || key;
  }, [lang]);

  // Memoize context value so consumers don't re-render unnecessarily
  const value = useMemo(() => ({ lang, setLanguage, t, LANGUAGES }), [lang, setLanguage, t]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};

export { LANGUAGES };
export default I18nProvider;
